/*
    * Document management mixin
    * Provides functionality for managing the document state, history, and file operations in the Mikomic editor.
*/

import M from 'minimatch';
import { markRaw } from 'vue';
export default {
    data() {
        return {

            //history management
            history_stack: [],        
            redo_stack: [],           
            max_history_length: 0,   
            is_performing_history_action: false,

            //document management
            current_file_path: null,
            document_has_unsaved_changes: false,
            recent_files: [],

            // MKC2 Container support
            container_data: null,
            current_page_index: 0,
            is_mkc2_container: false,
            is_switching_pages: false, // Flag to prevent multiple simultaneous page switches

            // File System Access API support
            supports_file_system_api: 'showSaveFilePicker' in window,
            current_file_handle: null,
            autosave_original_filename: null,

            encryption_key: null,
            document_metadata: null,

            autosave_interval: null, // We keep only the timer reference
            db: null,


            // Export modal properties
            show_export_dialog: false,
            export_format: 'png',
            export_quality: 0.9,
            export_pixel_ratio: 1,
            export_processing: false,

        };
    },


    computed: {

        calculated_final_resolution() {
            return {
                width: Math.round(this.canvas_width * this.export_pixel_ratio),
                height: Math.round(this.canvas_height * this.export_pixel_ratio)
            };
        },

        container_pages() {
            if (!this.is_mkc2_container || !this.container_data || !this.container_data.pages) {
                return [];
            }
            return this.container_data.pages;
        },

        current_page_data() {
            if (!this.is_mkc2_container || this.container_pages.length === 0) {
                return null;
            }
            return this.container_pages[this.current_page_index] || null;
        },

    },

    mounted() {
        this.set_history_length();
    },
    
    methods: {

        create_snapshot() {
            const panels_data = this.panels.map(panel => {
                if (!panel) return null; // Skip if panel is null or undefined

                const is_global = panel.getAttr('is_global') === true;
                const panel_copy = {
                    // Correctly call the id() method to get the string ID
                    id: panel.id() || '', 
                    x: panel.x(),
                    y: panel.y(),
                    points: [...panel.points],
                    panel_color: panel.background ? panel.background.fill() : this.panel_color,
                    is_global: is_global,
                    respect_margins: panel.getAttr('respect_margins') === true,
                    content: []
                };
                
                if (panel.content_group) {
                    // Get ALL object types from the content group including standalone text
                    const objects = panel.content_group.find(node => node.hasName('panel_image') || node.hasName('panel_balloon') || node.hasName('standalone_text'));

                    panel_copy.content = objects.map(obj => {
                        const object_type = obj.getAttr('object_type');
                        const common_data = {
                            // Correctly call the id() method for objects as well
                            id: obj.id(), 
                            layer_name: obj.getAttr('layer_name') || `Object ${obj.id()}`,
                            locked: obj.getAttr('locked') === true,
                            x: obj.x(),
                            y: obj.y(),
                            rotation: obj.rotation(),
                            scaleX: obj.scaleX(),
                            scaleY: obj.scaleY(),
                            visible: obj.visible(),
                            offsetX: obj.offsetX(),
                            offsetY: obj.offsetY(),
                            z_index: obj.zIndex(),
                            object_type: object_type,
                            blend_mode: obj.globalCompositeOperation() || 'normal',
                            opacity: obj.opacity() || 1
                        };

                        if (object_type === 'image') {
                            // ...existing image data...
                            return {
                                ...common_data,
                                src: obj.getAttr('src_data') || '',
                            };
                        } 
                        else if (object_type === 'balloon') {
                            // Ensure all text boxes in merged balloons have proper custom_box_for_recenter attributes
                            // before creating the snapshot
                            if (obj.texts && Array.isArray(obj.texts) && obj.texts.length > 1) {
                                this.ensure_all_text_boxes_have_custom_box_for_recenter(obj);
                            }

                            const balloon_shape = obj.shape;
                            // --- Cambios aquí: soporte para texts múltiples ---
                            let texts_data = null;
                            if (obj.texts && Array.isArray(obj.texts)) {
                                texts_data = obj.texts.map(text_node => {
                                    const snap_data = {
                                        text_content: text_node.text(),
                                        font_size: text_node.fontSize(),
                                        font_family: text_node.fontFamily(),
                                        font_style: text_node.fontStyle(),
                                        fill: text_node.fill(),
                                        stroke: text_node.stroke(),
                                        stroke_width: text_node.strokeWidth(),
                                        line_height: text_node.lineHeight(),
                                        align: text_node.align(),
                                        padding: text_node.padding(),
                                        x: text_node.x(),
                                        y: text_node.y(),
                                        offset_x: text_node.offsetX(),
                                        offset_y: text_node.offsetY(),
                                        scale_x: text_node.scaleX(),
                                        scale_y: text_node.scaleY(),
                                        width: text_node.width(),
                                        height: text_node.height(),
                                        wrap: 'word',
                                        custom_box_for_recenter: text_node.getAttr('custom_box_for_recenter') ? { ...text_node.getAttr('custom_box_for_recenter') } : null,
                                        // Store individual custom text box properties for each text_node
                                        custom_text_position: text_node.getAttr('custom_text_position') === true,
                                        custom_text_box_x: text_node.getAttr('custom_text_box_x'),
                                        custom_text_box_y: text_node.getAttr('custom_text_box_y'),
                                        custom_text_box_width: text_node.getAttr('custom_text_box_width'),
                                        custom_text_box_height: text_node.getAttr('custom_text_box_height')
                                    };
                                    // LOGGING:
                                    if (obj.texts.length > 1) { // Only for merged balloons
                                        //console.log(`[CreateSnapshot] Balloon ${obj.id()}, TextNode ID: ${text_node.id()}, custom_text_box_width: ${snap_data.custom_text_box_width}, custom_text_box_x: ${snap_data.custom_text_box_x}, custom_text_box_y: ${snap_data.custom_text_box_y}, custom_text_box_height: ${snap_data.custom_text_box_height}`);
                                    }
                                    return snap_data;
                                });
                            }
                            return {
                                ...common_data,
                                balloon_type: obj.getAttr('balloon_type') || 'speech',
                                // Deep copy of points is crucial
                                balloon_points: JSON.parse(JSON.stringify(obj.getAttr('balloon_points') || [])),
                                fill_opacity: obj.getAttr('fill_opacity') || 1,
                                original_stroke_width: (obj.getAttr('original_stroke_width') !== undefined && obj.getAttr('original_stroke_width') !== null) ? obj.getAttr('original_stroke_width') : this.preferences.balloon_defaults.stroke_width,
                                original_stroke_dash: obj.getAttr('original_stroke_dash') || '',
                                // Shape properties
                                shape_fill: balloon_shape ? balloon_shape.fill() : '#FFFFFF',
                                shape_stroke: balloon_shape ? balloon_shape.stroke() : '#000000',
                                shape_stroke_width: balloon_shape ? balloon_shape.strokeWidth() : this.preferences.balloon_defaults.stroke_width,
                                shape_stroke_dash: balloon_shape && balloon_shape.dash() ? [...balloon_shape.dash()] : [],
                                // --- Cambios aquí: texts múltiples o texto único ---
                                texts: texts_data,
                                text_content: obj.text ? obj.text.text() : '',
                                text_font_size: obj.text ? obj.text.fontSize() : this.preferences.text_defaults.font_size,
                                text_font_family: obj.text ? obj.text.fontFamily() : this.preferences.text_defaults.font_family,
                                text_font_style: obj.text ? obj.text.fontStyle() : this.preferences.text_defaults.font_style,
                                text_fill: obj.text ? obj.text.fill() : this.preferences.text_defaults.text_color,
                                text_stroke: obj.text ? obj.text.stroke() : this.preferences.text_defaults.text_stroke,
                                text_stroke_width: obj.text ? obj.text.strokeWidth() : this.preferences.text_defaults.text_stroke_width,
                                text_line_height: obj.text ? obj.text.lineHeight() : this.preferences.text_defaults.line_height,
                                text_align: obj.text ? obj.text.align() : this.preferences.text_defaults.align,
                                text_padding: obj.text ? obj.text.padding() : 10,
                                text_x: obj.text ? obj.text.x() : 0,
                                text_y: obj.text ? obj.text.y() : 0,
                                text_offset_x: obj.text ? obj.text.offsetX() : 0,
                                text_offset_y: obj.text ? obj.text.offsetY() : 0,
                                text_scale_x: obj.text ? obj.text.scaleX() : 1,
                                text_scale_y: obj.text ? obj.text.scaleY() : 1,
                                text_width: obj.text ? obj.text.width() : 'auto', // Save width ('auto' or number)
                                text_height: obj.text ? obj.text.height() : 'auto', // Save height
                                // Custom text position attributes
                                custom_text_position: obj.getAttr('custom_text_position') === true,
                                custom_text_box_x: obj.getAttr('custom_text_box_x'),
                                custom_text_box_y: obj.getAttr('custom_text_box_y'),
                                custom_text_box_width: obj.getAttr('custom_text_box_width'),
                                custom_text_box_height: obj.getAttr('custom_text_box_height'),
                                // Virtual balloon size for proper UI behavior
                                balloon_size: obj.getAttr('balloon_size') || { x: 1, y: 1 },
                                // Thought bubbles for thought pointer type
                                thought_bubbles: obj.getAttr('thought_bubbles') ? JSON.parse(JSON.stringify(obj.getAttr('thought_bubbles'))) : null,
                            };
                        } 
                        else if (object_type === 'standalone_text') {
                            const text_node = obj.findOne('Text');
                            return {
                                ...common_data,
                                text_content: text_node ? text_node.text() : '',
                                font_size: text_node ? text_node.fontSize() : this.preferences.text_defaults.font_size,
                                font_family: text_node ? text_node.fontFamily() : this.preferences.text_defaults.font_family,
                                font_style: text_node ? text_node.fontStyle() : this.preferences.text_defaults.font_style,
                                text_color: text_node ? text_node.fill() : this.preferences.text_defaults.text_color,
                                text_stroke: text_node ? text_node.stroke() : this.preferences.text_defaults.text_stroke,
                                text_stroke_width: text_node ? text_node.strokeWidth() : this.preferences.text_defaults.text_stroke_width,
                                line_height: text_node ? text_node.lineHeight() : this.preferences.text_defaults.line_height,
                                align: text_node ? text_node.align() : this.preferences.text_defaults.align,
                                // Custom text box properties
                                custom_text_position: obj.getAttr('custom_text_position') === true,
                                custom_text_box_x: obj.getAttr('custom_text_box_x'),
                                custom_text_box_y: obj.getAttr('custom_text_box_y'),
                                custom_text_box_width: obj.getAttr('custom_text_box_width'),
                                custom_text_box_height: obj.getAttr('custom_text_box_height'),
                            };
                        }
                        return null; // Should not happen if filtered correctly
                    }).filter(data => data !== null); // Filter out any null entries

                    // Sort by z-index
                    panel_copy.content.sort((a, b) => a.z_index - b.z_index);
                }
                return panel_copy;
            });

            return {
                panels_data,
                canvas_width: this.canvas_width,
                canvas_height: this.canvas_height,
                panel_color: this.panel_color,
                outline_width: this.outline_width,
                outline_color: this.outline_color,
                background_color: this.background_color,
                page_margin: this.page_margin,
                // Store selected object ID to potentially reselect after restore
                // Correctly call id() method here too
                selected_object_id: this.selected_object ? this.selected_object.id() : null, 
            };
        },

        // Push an action to history stack
        push_to_history(action_name) {
            //console.log(`Pushing action to history: ${action_name}`);
            // No registration if performing history action
            if (this.is_performing_history_action) return;
            
            //console.log(`Recording action: ${action_name}`);
            
            // Create and save snapshot
            const snapshot = this.create_snapshot();
            snapshot.action_name = action_name;
            
            // Add to history
            this.history_stack.push(snapshot);
            
            // Limit history size
            if (this.history_stack.length > this.max_history_length) {
                this.history_stack.shift();
            }
            
            // Clear redo stack when there is a new action
            this.redo_stack = [];
            
            // Mark document as having unsaved changes
            this.document_has_unsaved_changes = true;
            
            //console.log(`Action '${action_name}' added to history, stack size: ${this.history_stack.length}`);
        },

        // Updated undo function
        undo() {
            if (this.history_stack.length <= 1) {
                console.log('Nothing to undo');
                return;
            }

            // Remove current state and push it onto redo stack
            const current_state = this.history_stack.pop();
            this.redo_stack.push(current_state);

            // The new top of history_stack is the previous state we want to restore
            const previous_state = this.history_stack[this.history_stack.length - 1];

            this.restore_from_snapshot(previous_state);
            
            // Update changes state
            this.document_has_unsaved_changes = true;
            this.deselect_object(); // Ensure deselection after undo
            //console.log(`Undo executed, history stack size: ${this.history_stack.length}, redo stack size: ${this.redo_stack.length}`);
        },

        // Updated redo function
        redo() {
            if (this.redo_stack.length === 0) {
                console.log('Nothing to redo');
                return;
            }

            // Pop the last redo state and push it onto history stack
            const redo_state = this.redo_stack.pop();
            this.history_stack.push(redo_state);

            this.restore_from_snapshot(redo_state);
            
            // Update changes state
            this.document_has_unsaved_changes = true;
            
            //console.log(`Redo executed, history stack size: ${this.history_stack.length}, redo stack size: ${this.redo_stack.length}`);
        },
        
        // Restore state from a snapshot
        restore_from_snapshot(snapshot) {
            //console.log(`Restoring snapshot (Action: ${snapshot.action_name || 'N/A'}) with ${snapshot.panels_data ? snapshot.panels_data.length : 0} panels`);
            this.is_performing_history_action = true; // Set flag at start

            try {
                // --- Clean current state ---
                this.deselect_all(); // Includes deselect_object which calls exit_balloon_edit_mode
                // Ensure edit mode is fully exited *before* destroying panels
                if (this.balloon_edit_mode_active) {
                     this.exit_balloon_edit_mode();
                }

                // Remove all current panels and clear layer
                this.panels.forEach(panel => panel.destroy());
                this.panels = [];
                this.panels_layer.destroyChildren(); // Clear everything from the layer

                // --- Restore basic properties ---
                // ... (canvas_width, height, colors, margin, etc.) ...
                this.canvas_width = snapshot.canvas_width;
                this.canvas_height = snapshot.canvas_height;
                this.panel_color = snapshot.panel_color;
                this.outline_width = snapshot.outline_width;
                this.outline_color = snapshot.outline_color || '#000000';
                this.background_color = snapshot.background_color;
                this.page_margin = snapshot.page_margin;
                
                // Recreate background
                const background = new Konva.Rect({
                    x: 0,
                    y: 0,
                    width: this.canvas_width,
                    height: this.canvas_height,
                    fill: this.background_color,
                    name: 'background'
                });
                this.panels_layer.add(background);

                // --- Recreate panels and their content ---
                const recreation_promises = [];
                if (snapshot.panels_data && snapshot.panels_data.length > 0) {
                    snapshot.panels_data.forEach(panel_data => {
                        // recreate_panel_from_data returns promises now
                        recreation_promises.push(this.recreate_panel_from_data(panel_data));
                    });
                }

                // Wait for all panels and their contents (including async image loads) to be ready
                Promise.all(recreation_promises).then(() => {
                    // --- Final steps after all content is loaded ---
                    
                    // Ensure a valid panel selection after restoration
                    this.selected_panel = null; // Reset first
                    
                    // Find and select the global panel or the main content panel
                    const global_panel = this.panels.find(panel => panel.getAttr('is_global') === true);
                    if (global_panel) {
                        this.selected_panel = global_panel;
                    } else if (this.panels.length > 0) {
                        // If no global panel, use the first available panel
                        this.selected_panel = this.panels[0];  
                    }
                    
                    this.redraw();

                    // Attempt to reselect the object
                    if (snapshot.selected_object_id) {
                        this.$nextTick(() => {
                            const object_to_reselect = this.stage.findOne('#' + snapshot.selected_object_id);
                            if (object_to_reselect) {
                                this.select_object(object_to_reselect);
                                // Force UI update after reselection to sync computed properties
                                this.update_ui_from_selected_object();
                            } else {
                                this.deselect_object();
                                this.update_ui_from_selected_object();
                            }
                            
                            // Enable exterior stroke for restored texts
                            if (typeof this.enable_exterior_stroke_for_existing_texts === 'function') {
                                this.enable_exterior_stroke_for_existing_texts();
                            }
                            
                            this.is_performing_history_action = false; // Reset flag after completion
                            this.redraw();
                        });
                    } else {
                        this.deselect_object();
                        this.update_ui_from_selected_object();
                        
                        // Enable exterior stroke for restored texts
                        if (typeof this.enable_exterior_stroke_for_existing_texts === 'function') {
                            this.enable_exterior_stroke_for_existing_texts();
                        }
                        
                        this.is_performing_history_action = false; // Reset flag
                        this.redraw();
                    }
                }).catch(error => {
                    console.error("Error during snapshot restoration:", error);
                    this.is_performing_history_action = false; // Reset flag on error
                });
            } catch (error) {
                console.error("Error during snapshot restoration:", error);
                this.is_performing_history_action = false; // Reset flag on error
            }
        },

        // Helper method to recreate a panel from saved data
        async recreate_panel_from_data(panel_data) {
            // Create new panel group
            const panel = new Konva.Group({
                x: panel_data.x,
                y: panel_data.y,
                name: 'panel_group',
                id: panel_data.id || 'panel_' + Date.now()
            });

            // Add panel attribute for identification
            panel.setAttr('is_panel', true);

            // Restaurar el atributo respect_margins si existe en los datos, default true
            panel.setAttr('respect_margins', panel_data.respect_margins !== undefined ? panel_data.respect_margins : true);

            // Restaurar el atributo is_global si existe en los datos
            if (panel_data.is_global === true) {
                panel.setAttr('is_global', true);
                panel.listening(true);
            }
            
            // Recreate panel elements
            let background; 

            // Only create background for non-global panels
            if (!panel_data.is_global) {
                // Recreate panel elements
                background = new Konva.Line({
                    points: panel_data.points,
                    closed: true,
                    fill: panel_data.panel_color || this.panel_color,
                    name: 'panel_background'
                });
                panel.add(background);
                panel.background = background;
            }
            
            // Content group
            const content_group = new Konva.Group({
                name: 'content_group',
                listening: true
            });
            
            // Set up clipping
            content_group.clipFunc((ctx) => {
                ctx.beginPath();
                for (let i = 0; i < panel_data.points.length; i += 2) {
                    const x = panel_data.points[i];
                    const y = panel_data.points[i + 1];
                    if (i === 0) ctx.moveTo(x, y);
                    else ctx.lineTo(x, y);
                }
                ctx.closePath();
            });
            panel.add(content_group);
            
            // Add outline
            const outline = new Konva.Line({
                points: panel_data.points,
                closed: true,
                stroke: panel_data.is_global ? 'rgba(0,0,0,0)' : this.outline_color,
                strokeWidth: this.outline_width,
                fill: null,
                name: 'panel_outline',
                listening: false
            });
            panel.add(outline);
            
            // Store references
            panel.outline = outline;
            panel.background = background;
            panel.content_group = content_group;
            panel.points = panel_data.points;
            
            // Add panel to layer
            this.panels_layer.add(panel);
            this.panels.push(panel);

            // Recreate content (images and balloons)
            const content_promises = [];
            if (panel_data.content && panel_data.content.length > 0) {
                // Sort content by z_index *before* creating elements to help with initial order
                const sorted_content = [...panel_data.content].sort((a, b) => (a.z_index || 0) - (b.z_index || 0));
                sorted_content.forEach(obj_data => { // Use sorted data
                    if (obj_data.object_type === 'image') {
                        content_promises.push(this.recreate_image_from_data(obj_data, content_group));
                    } else if (obj_data.object_type === 'balloon') {
                        // Pass content_group explicitly
                        content_promises.push(this.recreate_balloon_from_data(obj_data, content_group));
                    } else if (obj_data.object_type === 'standalone_text') {
                        content_promises.push(this.recreate_standalone_text_from_data(obj_data, content_group));
                    }
                });
            }

            // Wait for content specific to this panel
            await Promise.all(content_promises);

            // --- Apply Z-index after all children are added ---
            // Get children that have a saved z_index
            const children_to_sort = content_group.getChildren().filter(node => node.getAttr('saved_z_index') !== undefined);

            // Sort them based on the saved z_index
            children_to_sort.sort((a, b) => a.getAttr('saved_z_index') - b.getAttr('saved_z_index'));

            // Apply the final zIndex based on the sorted order
            children_to_sort.forEach((node, index) => {
                // Set zIndex based on its position in the sorted array
                // This ensures correct relative order among these elements
                node.zIndex(index);
                // Optional: Clean up the temporary attribute
                // node.setAttr('saved_z_index', undefined);
            });
            // --- End Z-index application ---


            // Ensure global panel is always on top after restoration
            if (panel_data.is_global) {
                panel.moveToTop();
            }
            
            return panel;
        },

        // Helper method to recreate images from saved data
        recreate_image_from_data(img_data, content_group) {
            return new Promise((resolve, reject) => {
                if (!img_data.src) {
                    console.warn("Skipping image recreation: no src data", img_data.id);
                    resolve(); // Resolve immediately if no source
                    return;
                }

                const image = new Image();            
                
                image.onload = () => {
                    const konva_image_raw = new Konva.Image({
                        // ... (set properties from img_data as before) ...
                        x: img_data.x, y: img_data.y, image: image,
                        rotation: img_data.rotation || 0,
                        scaleX: img_data.scaleX || 1, scaleY: img_data.scaleY || 1,
                        name: 'panel_image', id: img_data.id || 'img_' + Date.now(),
                        draggable: this.active_tool === 'select' && !img_data.locked,
                        listening: !img_data.locked, // Control event listening based on locked state
                        visible: img_data.visible !== undefined ? img_data.visible : true,
                        globalCompositeOperation: img_data.blend_mode || 'normal',
                        opacity: img_data.opacity !== undefined ? img_data.opacity : 1,
                        offset: { x: img_data.offsetX || 0, y: img_data.offsetY || 0 }
                    });
                    const konva_image = markRaw(konva_image_raw);

                    // ... (set attributes: src_data, layer_name, locked, object_type) ...
                    konva_image.setAttr('src_data', img_data.src);
                    konva_image.setAttr('layer_name', img_data.layer_name || `Layer ${konva_image.id()}`);
                    konva_image.setAttr('locked', img_data.locked === true);
                    konva_image.setAttr('object_type', 'image');

                    // Store the intended z_index but don't apply it yet
                    if (img_data.z_index !== undefined) {
                        konva_image.setAttr('saved_z_index', img_data.z_index);
                    }


                    content_group.add(konva_image);
                    // --- REMOVED zIndex setting from here ---
                    // if (img_data.z_index !== undefined) konva_image.zIndex(img_data.z_index);
                    // else konva_image.zIndex(content_group.getChildren().length - 1); // Ensure zIndex if not saved

                    resolve(konva_image);
                };
                image.onerror = (err) => {
                    console.error("Error loading image source during recreation:", img_data.src, err);
                    reject(err); // Reject the promise on error
                };
                image.src = img_data.src;
            });
        },

        // New function to recreate balloons
        async recreate_balloon_from_data(balloon_data, content_group, skip_history_record = false) {
            return new Promise((resolve) => {
                const temp_options = {
                    x: balloon_data.x || 0,
                    y: balloon_data.y || 0,
                    layer_name: balloon_data.layer_name,
                    skip_history_record: skip_history_record
                };

                const original_selected_panel = this.selected_panel;
                this.selected_panel = content_group.getParent();
                const balloon_group = this.create_balloon(balloon_data.balloon_type || 'speech', temp_options);

                this.selected_panel = original_selected_panel;

                if (!balloon_group) {
                    resolve(null);
                    return;
                }

                balloon_group.id(balloon_data.id);
                balloon_group.rotation(balloon_data.rotation || 0);
                balloon_group.scaleX(balloon_data.scaleX || 1);
                balloon_group.scaleY(balloon_data.scaleY || 1);
                balloon_group.visible(balloon_data.visible !== undefined ? balloon_data.visible : true);
                balloon_group.globalCompositeOperation(balloon_data.blend_mode || 'normal');
                balloon_group.opacity(balloon_data.opacity !== undefined ? balloon_data.opacity : 1);
                balloon_group.offsetX(balloon_data.offsetX || 0);
                balloon_group.offsetY(balloon_data.offsetY || 0);
                balloon_group.setAttr('locked', balloon_data.locked === true);
                balloon_group.setAttr('fill_opacity', balloon_data.fill_opacity || 1);
                balloon_group.setAttr('original_stroke_width', (balloon_data.original_stroke_width !== undefined && balloon_data.original_stroke_width !== null) ? balloon_data.original_stroke_width : this.preferences.balloon_defaults.stroke_width);
                balloon_group.setAttr('original_stroke_dash', balloon_data.original_stroke_dash || '');
                // Restore virtual balloon size for proper UI behavior
                balloon_group.setAttr('balloon_size', balloon_data.balloon_size || { x: 1, y: 1 });

                balloon_group.draggable(!balloon_data.locked);
                balloon_group.listening(!balloon_data.locked); // Control event listening based on locked state

                const restored_points = JSON.parse(JSON.stringify(balloon_data.balloon_points || []));
                balloon_group.setAttr('balloon_points', restored_points);
                if (balloon_group.shape && restored_points.length > 0) {
                    const new_path_data = this.generate_balloon_path_from_points(restored_points);
                    balloon_group.shape.data(new_path_data);
                    balloon_group.shape.fill(balloon_data.shape_fill || '#FFFFFF');
                    balloon_group.shape.stroke(balloon_data.shape_stroke || '#000000');
                    balloon_group.shape.strokeWidth((balloon_data.shape_stroke_width !== undefined && balloon_data.shape_stroke_width !== null) ? balloon_data.shape_stroke_width : this.preferences.balloon_defaults.stroke_width);
                    // Apply dash pattern - use shape_stroke_dash if available, otherwise fall back to original_stroke_dash
                    const dash_pattern = balloon_data.shape_stroke_dash || balloon_data.original_stroke_dash;
                    if (dash_pattern && Array.isArray(dash_pattern) && dash_pattern.length > 0) {
                        balloon_group.shape.dash(dash_pattern);
                    } else if (dash_pattern && typeof dash_pattern === 'string' && dash_pattern.length > 0) {
                        balloon_group.shape.dash(dash_pattern.split(',').map(Number));
                    } else {
                        balloon_group.shape.dash([]);
                    }
                }                // Restore thought bubbles if they exist
                if (balloon_data.thought_bubbles) {
                    const restored_thought_bubbles = JSON.parse(JSON.stringify(balloon_data.thought_bubbles));
                    balloon_group.setAttr('thought_bubbles', restored_thought_bubbles);
                    // Update the shape to include the thought bubbles
                    if (typeof this.update_balloon_shape_with_thought_bubbles === 'function') {
                        this.update_balloon_shape_with_thought_bubbles(balloon_group);
                    }
                }

                // ...existing code...
                if (balloon_data.texts && Array.isArray(balloon_data.texts) && balloon_data.texts.length > 0) {
                    // Elimina el texto único si existe
                    if (balloon_group.text) {
                        balloon_group.text.destroy();
                        balloon_group.text = null;
                    }
                    balloon_group.texts = [];
                    balloon_data.texts.forEach(text_data => {
                        const text_node = new Konva.Text({
                            text: text_data.text_content || '',
                            fontSize: text_data.font_size || this.preferences.text_defaults.font_size,
                            fontFamily: text_data.font_family || this.preferences.text_defaults.font_family,
                            fontStyle: text_data.font_style || this.preferences.text_defaults.font_style,
                            fill: text_data.fill || this.preferences.text_defaults.text_color,
                            stroke: text_data.stroke || this.preferences.text_defaults.text_stroke,
                            strokeWidth: text_data.stroke_width !== undefined ? text_data.stroke_width : this.preferences.text_defaults.text_stroke_width,
                            lineHeight: text_data.line_height !== undefined ? text_data.line_height : this.preferences.text_defaults.line_height,
                            align: text_data.align || this.preferences.text_defaults.align,
                            fillAfterStrokeEnabled: true,
                            padding: text_data.padding !== undefined ? text_data.padding : 10,
                            x: text_data.x || 0,
                            y: text_data.y || 0,
                            offsetX: text_data.offset_x || 0,
                            offsetY: text_data.offset_y || 0,
                            scaleX: text_data.scale_x || 1,
                            scaleY: text_data.scale_y || 1,
                            width: text_data.width || 'auto',
                            wrap: 'word',
                            ellipsis: false,
                            listening: false,
                            name: 'balloon_text'
                        });
                        if (text_data.custom_box_for_recenter) {
                            text_node.setAttr('custom_box_for_recenter', text_data.custom_box_for_recenter);
                        }

                        // Restore custom text box attributes for THIS specific text_node
                        text_node.setAttr('custom_text_position', text_data.custom_text_position === true);
                        // Ensure we only try to set these if they were actually saved or intended to be custom
                        if (text_data.custom_text_position === true || (text_data.custom_text_box_width !== undefined && text_data.custom_text_box_width !== null)) {
                            text_node.setAttr('custom_text_box_x', text_data.custom_text_box_x);
                            text_node.setAttr('custom_text_box_y', text_data.custom_text_box_y);
                            text_node.setAttr('custom_text_box_width', text_data.custom_text_box_width);
                            text_node.setAttr('custom_text_box_height', text_data.custom_text_box_height);

                            // LOGGING:
                            if (balloon_data.texts && balloon_data.texts.length > 1) { // Only for merged balloons
                                console.log(`[RecreateBalloon] Balloon ${balloon_data.id}, TextNode (recreated), custom_text_box_width set to: ${text_data.custom_text_box_width}, read back: ${text_node.getAttr('custom_text_box_width')}, custom_text_box_x set to: ${text_data.custom_text_box_x}, read back: ${text_node.getAttr('custom_text_box_x')}, custom_text_box_y set to: ${text_data.custom_text_box_y}, read back: ${text_node.getAttr('custom_text_box_y')}, custom_text_box_height set to: ${text_data.custom_text_box_height}, read back: ${text_node.getAttr('custom_text_box_height')}`);
                            }
                        }

                        balloon_group.add(text_node);
                        balloon_group.texts.push(text_node);
                    });
                } 
                else if (balloon_group.text) {
                    // ...restaurar texto único como antes...
                    const text_node = balloon_group.text;
                    text_node.name('balloon_text');
                    text_node.listening(false);
                    text_node.text(balloon_data.text_content || '');
                    text_node.fontSize(balloon_data.text_font_size || this.preferences.text_defaults.font_size);
                    text_node.fontFamily(balloon_data.text_font_family || this.preferences.text_defaults.font_family);
                    text_node.fontStyle(balloon_data.text_font_style || this.preferences.text_defaults.font_style);
                    text_node.fill(balloon_data.text_fill || this.preferences.text_defaults.text_color);
                    text_node.stroke(balloon_data.text_stroke || '#FFFFFF');
                    text_node.strokeWidth(balloon_data.text_stroke_width !== undefined ? balloon_data.text_stroke_width : 0);
                    text_node.lineHeight(balloon_data.text_line_height !== undefined ? balloon_data.text_line_height : 1);
                    text_node.align(balloon_data.text_align || 'center');
                    text_node.padding(balloon_data.text_padding !== undefined ? balloon_data.text_padding : 10);
                    text_node.x(balloon_data.text_x || 0);
                    text_node.y(balloon_data.text_y || 0);
                    text_node.offsetX(balloon_data.text_offset_x || 0);
                    text_node.offsetY(balloon_data.text_offset_y || 0);
                    balloon_group.text.scaleX(balloon_data.scale_x || 1);
                    balloon_group.text.scaleY(balloon_data.scale_y || 1);
                    balloon_group.text.width(balloon_data.width || 'auto'); // Save width ('auto' or number)
                    // balloon_group.text.height(balloon_data.height); 
                    balloon_group.text.wrap('word');
                    balloon_group.text.ellipsis(false);
                     if (balloon_data.custom_box_for_recenter) {
                        balloon_group.text.setAttr('custom_box_for_recenter', { ...balloon_data.custom_box_for_recenter });
                    }
                }
                
                

                balloon_group.setAttr('custom_text_position', balloon_data.custom_text_position === true);
                if (balloon_data.custom_text_position) {
                    balloon_group.setAttr('custom_text_box_x', balloon_data.custom_text_box_x);
                    balloon_group.setAttr('custom_text_box_y', balloon_data.custom_text_box_y);
                    balloon_group.setAttr('custom_text_box_width', balloon_data.custom_text_box_width);
                    balloon_group.setAttr('custom_text_box_height', balloon_data.custom_text_box_height);
                }

                if (balloon_group.getParent() !== content_group) {
                    balloon_group.moveTo(content_group);
                }

                if (balloon_data.z_index !== undefined) {
                    balloon_group.setAttr('saved_z_index', balloon_data.z_index);
                }
                

                
                this.$nextTick(() => {
                    this.recenter_balloon_text(balloon_group);
                });

                resolve(balloon_group);
            });
        },
        // Helper method to recreate standalone text from saved data
        async recreate_standalone_text_from_data(text_data, content_group) {
            return new Promise((resolve) => {
                // Create text group manually without applying offset logic
                const text_group_raw = new Konva.Group({
                    x: text_data.x || 0,
                    y: text_data.y || 0,
                    draggable: true,
                    name: 'standalone_text',
                    id: text_data.id,
                });

                // Mark as raw to prevent Vue reactivity
                const text_group = markRaw(text_group_raw);

                // Create invisible text box
                const text_box = new Konva.Rect({
                    x: text_data.custom_text_box_x || -75,
                    y: text_data.custom_text_box_y || -25,
                    width: text_data.custom_text_box_width || 250,
                    height: text_data.custom_text_box_height || 150,
                    fill: 'transparent',
                    stroke: 'transparent',
                    name: 'text_box',
                    listening: false,
                });
                
                // Create text content
                const text_node = new Konva.Text({
                    text: text_data.text_content || 'Select a tool or double-click to edit',
                    fontSize: text_data.font_size || this.preferences.text_defaults.font_size,
                    fontFamily: text_data.font_family || this.preferences.text_defaults.font_family,
                    fontStyle: text_data.font_style || this.preferences.text_defaults.font_style,
                    fill: text_data.text_color || this.preferences.text_defaults.text_color,
                    stroke: text_data.text_stroke || this.preferences.text_defaults.text_stroke,
                    strokeWidth: text_data.text_stroke_width || this.preferences.text_defaults.text_stroke_width,
                    lineHeight: text_data.line_height !== undefined ? text_data.line_height : 1,
                    align: text_data.align || 'center',
                    fillAfterStrokeEnabled: true,
                    x: 0,
                    y: 0,
                    name: 'text_content',
                    wrap: 'word',
                    ellipsis: false,
                    listening: true
                });

                // Add elements to group
                text_group.add(text_box);
                text_group.add(text_node);
                
                // Set basic attributes
                text_group.setAttr('object_type', 'standalone_text');
                text_group.setAttr('layer_name', text_data.layer_name || `Text ${text_group.id()}`);
                text_group.setAttr('locked', text_data.locked === true);
                
                // Add references
                text_group.text_box = text_box;
                text_group.text = text_node;

                // Restore all saved properties exactly as they were
                text_group.rotation(text_data.rotation || 0);
                text_group.scaleX(text_data.scaleX || 1);
                text_group.scaleY(text_data.scaleY || 1);
                text_group.visible(text_data.visible !== undefined ? text_data.visible : true);
                text_group.globalCompositeOperation(text_data.blend_mode || 'normal');
                text_group.opacity(text_data.opacity !== undefined ? text_data.opacity : 1);
                
                // Restore offset exactly as saved (coordinates already account for offset)
                text_group.offsetX(text_data.offsetX || 0);
                text_group.offsetY(text_data.offsetY || 0);
                text_group.draggable(!text_data.locked);
                text_group.listening(!text_data.locked); // Control event listening based on locked state

                // Restore custom text box properties
                if (text_data.custom_text_position === true) {
                    text_group.setAttr('custom_text_position', true);
                    text_group.setAttr('custom_text_box_x', text_data.custom_text_box_x);
                    text_group.setAttr('custom_text_box_y', text_data.custom_text_box_y);
                    text_group.setAttr('custom_text_box_width', text_data.custom_text_box_width);
                    text_group.setAttr('custom_text_box_height', text_data.custom_text_box_height);
                }

                // Add transform event listener
                text_group.on('transform', () => {
                    this.recenter_standalone_text(text_group);
                });
                
                // Add to content group
                content_group.add(text_group);
                
                // Store z-index for later application
                if (text_data.z_index !== undefined) {
                    text_group.setAttr('saved_z_index', text_data.z_index);
                }
                
                // Recenter text to ensure proper positioning
                this.$nextTick(() => {
                    this.recenter_standalone_text(text_group);
                });                
                resolve(text_group);
            });
        },

        async serialize_document(gallery_item_data = null, is_autosave = false) {
            // Create snapshot with current data
            const snapshot = this.create_snapshot();
            
            // Create document object (MKC1 format structure)
            const document_data = {
                format_version: "1.0",
                created_at: new Date().toISOString(),
                last_modified: new Date().toISOString(),
                document: {
                    canvas: {
                        width: snapshot.canvas_width,
                        height: snapshot.canvas_height,
                        background_color: snapshot.background_color,
                        outline_color: snapshot.outline_color,
                        outline_width: snapshot.outline_width,
                        page_margin: snapshot.page_margin,
                        format_id: this.current_format
                    },
                    panels: snapshot.panels_data
                }
            };

            // All documents are MKC2 containers - no retrocompatibility
            if (!this.is_mkc2_container || !this.container_data) {
                throw new Error("Invalid state: Application only supports MKC2 containers");
            }

            // Choose which container data to work with based on autosave flag
            let working_container_data;
            if (is_autosave) {
                // Create autosave copy without modifying original
                if (!this.container_data_autosave) {
                    this.container_data_autosave = JSON.parse(JSON.stringify(this.container_data));
                }
                working_container_data = this.container_data_autosave;
            } else {
                working_container_data = this.container_data;
            }

            // Update current page data in container
            if (working_container_data.pages && working_container_data.pages[this.current_page_index]) {
                working_container_data.pages[this.current_page_index].mkc_data = document_data;
                working_container_data.pages[this.current_page_index].page_last_modified = new Date().toISOString();
            } else {
                console.error("Cannot update page data: page does not exist at index", this.current_page_index);
                throw new Error("Invalid container state: current page does not exist");
            }
            
            // Update container last modified
            working_container_data.last_modified = new Date().toISOString();
            
            // Generate thumbnail for current page (only for manual saves, not autosaves)
            if (!is_autosave) {
                const thumbnail_base64 = await this.generate_document_thumbnail_base64();
                
                // Store thumbnail in current page (always update on save)
                if (thumbnail_base64) {
                    working_container_data.pages[this.current_page_index].thumbnail = thumbnail_base64;
                }
            }
            
            // CRITICAL: Container thumbnail is ALWAYS page 1 thumbnail (regardless of current page)
            let container_thumbnail = null;
            
            // Always use page 1 thumbnail for container (never use current page as fallback)
            if (working_container_data.pages[0] && working_container_data.pages[0].thumbnail) {
                container_thumbnail = working_container_data.pages[0].thumbnail;
            } else if (!is_autosave) {
                // Only generate new thumbnail for manual saves, not autosaves
                if (this.current_page_index === 0) {
                    // If we're saving page 1 manually and generated a thumbnail above, use it
                    const thumbnail_base64 = await this.generate_document_thumbnail_base64();
                    if (thumbnail_base64) {
                        working_container_data.pages[0].thumbnail = thumbnail_base64;
                        container_thumbnail = thumbnail_base64;
                    }
                } else {
                    // This should not happen - log error and use empty thumbnail
                    console.error("Page 1 has no thumbnail for container metadata");
                    container_thumbnail = null;
                }
            } else {
                // For autosaves, use existing thumbnail or null
                container_thumbnail = null;
            }
            
            // Create container metadata (use first page for compatibility)
            const first_page_data = working_container_data.pages[0];
            const canvas_dimensions = `${first_page_data.mkc_data.document.canvas.width}x${first_page_data.mkc_data.document.canvas.height}`;
            const dimension_hash = CryptoJS.SHA256(canvas_dimensions).toString().substring(0, 16);
            
            this.document_metadata = {
                version: "2.0",
                dimension_hash: dimension_hash,
                timestamp: Date.now(),
                document_thumbnail: container_thumbnail,
                container_created_at: working_container_data.created_at,
                container_last_modified: working_container_data.last_modified,
                container_pages_count: working_container_data.pages.length
            };
            
            // Request signature from server
            const signature = await this.request_document_signature(this.document_metadata);
            if (!signature) {
                throw new Error("Cannot save: This document uses premium features but your account is not premium");
            }
            
            // Encrypt container content
            const container_string = JSON.stringify(working_container_data);
            const encrypted_content = CryptoJS.AES.encrypt(container_string, signature).toString();
            
            // Create MKC2 document
            const mkc2_document = {
                header: "MKC2",
                metadata: this.document_metadata,
                signature: signature,
                content: encrypted_content,
                ...(gallery_item_data && {
                    gallery_item: {
                        type: gallery_item_data.type || 'preset',
                        subtype: gallery_item_data.subtype || 'general',
                        konva_data: gallery_item_data.konva_data || null,
                        thumbnail_base64: gallery_item_data.thumbnail_base64 || null,
                        is_premium: gallery_item_data.is_premium || true
                    }
                })
            };
            
            return mkc2_document;
        },

        async generate_document_thumbnail_base64() {
            try {
                // Store current selections to restore later
                const previous_selected_panel = this.selected_panel;
                const previous_selected_object = this.selected_object;
                
                // Temporarily deselect everything to avoid blue strokes in thumbnail
                this.deselect_all();
                
                // Force redraw to ensure selection changes take effect
                if (this.stage) {
                    this.stage.draw();
                }
                
                // Add small delay to ensure deselection is fully processed
                await new Promise(resolve => setTimeout(resolve, 50));
                
                // Create temporary stage for thumbnail using DOCUMENT PAGE SIZE, not content bounds
                const temp_stage = new Konva.Stage({
                    container: document.createElement('div'),
                    width: 400,
                    height: 400
                });
                
                const temp_layer = new Konva.Layer();
                temp_stage.add(temp_layer);
                
                // Create a group with all visible objects from all layers (except UI)
                const group = new Konva.Group();
                
                this.stage.children.forEach(layer => {
                    if (layer !== this.ui_layer && layer.isVisible()) {
                        layer.getChildren().forEach(child => {
                            if (child.isVisible()) {
                                const cloned = child.clone();
                                group.add(cloned);
                            }
                        });
                    }
                });
                
                if (group.children.length === 0) {
                    temp_stage.destroy();
                    
                    // Restore previous selections
                    if (previous_selected_panel) {
                        this.selected_panel = previous_selected_panel;
                    }
                    if (previous_selected_object) {
                        this.selected_object = previous_selected_object;
                    }
                    
                    return null;
                }
                
                // Use DOCUMENT PAGE DIMENSIONS instead of content bounds
                const page_bounds = {
                    x: 0,
                    y: 0,
                    width: this.canvas_width,
                    height: this.canvas_height
                };
                
                // Scale to fit in FULL 400x400 - NO MARGIN AT ALL
                const scale = Math.min(400 / page_bounds.width, 400 / page_bounds.height);
                group.scale({ x: scale, y: scale });
                
                // Center the page properly in 400x400 canvas
                group.position({
                    x: (400 - page_bounds.width * scale) / 2,
                    y: (400 - page_bounds.height * scale) / 2
                });
                
                temp_layer.add(group);
                temp_layer.draw();
                
                // Generate base64 thumbnail
                const thumbnail_base64 = temp_stage.toDataURL({
                    mimeType: 'image/png',
                    quality: 0.8
                });
                
                // Cleanup
                temp_stage.destroy();
                
                // Restore previous selections after thumbnail generation
                if (previous_selected_panel) {
                    this.selected_panel = previous_selected_panel;
                }
                if (previous_selected_object) {
                    this.selected_object = previous_selected_object;
                }
                
                // Force redraw to restore selection visuals
                if (this.stage) {
                    this.stage.draw();
                }
                
                // Return only the base64 data without the data URL prefix
                return thumbnail_base64.replace(/^data:image\/png;base64,/, '');
            } catch (error) {
                console.error('Error generating document thumbnail:', error);
                return null;
            }
        },

        async generate_document_thumbnail() {
            try {
                // Store current selections to restore later
                const previous_selected_panel = this.selected_panel;
                const previous_selected_object = this.selected_object;
                
                // Temporarily deselect everything to avoid blue strokes in thumbnail
                this.deselect_all();
                
                // Force redraw to ensure selection changes take effect
                if (this.stage) {
                    this.stage.draw();
                }
                
                // Add small delay to ensure deselection is fully processed
                await new Promise(resolve => setTimeout(resolve, 50));
                
                // Create temporary stage for thumbnail using DOCUMENT PAGE SIZE, not content bounds
                const temp_stage = new Konva.Stage({
                    container: document.createElement('div'),
                    width: 400,
                    height: 400
                });
                
                const temp_layer = new Konva.Layer();
                temp_stage.add(temp_layer);
                
                // Create a group with all visible objects from all layers (except UI)
                const group = new Konva.Group();
                
                this.stage.children.forEach(layer => {
                    if (layer !== this.ui_layer && layer.isVisible()) {
                        layer.getChildren().forEach(child => {
                            if (child.isVisible()) {
                                const cloned = child.clone();
                                group.add(cloned);
                            }
                        });
                    }
                });
                
                if (group.children.length === 0) {
                    temp_stage.destroy();
                    
                    // Restore previous selections
                    if (previous_selected_panel) {
                        this.selected_panel = previous_selected_panel;
                    }
                    if (previous_selected_object) {
                        this.selected_object = previous_selected_object;
                    }
                    
                    return null;
                }
                
                // Use DOCUMENT PAGE DIMENSIONS instead of content bounds
                const page_bounds = {
                    x: 0,
                    y: 0,
                    width: this.canvas_width,
                    height: this.canvas_height
                };
                
                // Scale to fit in FULL 400x400 - NO MARGIN AT ALL
                const scale = Math.min(400 / page_bounds.width, 400 / page_bounds.height);
                group.scale({ x: scale, y: scale });
                
                // Center the page properly in 400x400 canvas
                group.position({
                    x: (400 - page_bounds.width * scale) / 2,
                    y: (400 - page_bounds.height * scale) / 2
                });
                
                temp_layer.add(group);
                temp_layer.draw();
                
                // Generate base64 thumbnail
                const thumbnail_base64 = temp_stage.toDataURL({
                    mimeType: 'image/png',
                    quality: 0.8
                });
                
                // Cleanup
                temp_stage.destroy();
                
                // Restore previous selections after thumbnail generation
                if (previous_selected_panel) {
                    this.selected_panel = previous_selected_panel;
                }
                if (previous_selected_object) {
                    this.selected_object = previous_selected_object;
                }
                
                // Force redraw to restore selection visuals
                if (this.stage) {
                    this.stage.draw();
                }
                
                return {
                    type: 'template',
                    subtype: 'document',
                    konva_data: null,
                    thumbnail_base64: thumbnail_base64.replace(/^data:image\/png;base64,/, ''),
                    is_premium: false
                };
            } catch (error) {
                console.error('Error generating document thumbnail:', error);
                
                return {
                    type: 'template',
                    subtype: 'document',
                    konva_data: null,
                    thumbnail_base64: null,
                    is_premium: false
                };
            }
        },

        async request_document_signature(metadata) {
            const form_data = new FormData();
            form_data.append('request', "sign_document");
            form_data.append('metadata', JSON.stringify(metadata));
            
            try {
                const response = await axios.post('ajax.php', form_data, {
                    headers: {'Content-Type': 'multipart/form-data'}
                });
                
                if (response.data.status === "success") {
                    return response.data.signature;
                } else {
                    this.show_error_message(
                        this.get_t('document.premium_feature_limited'), 
                        response.data.message || this.get_t('document.premium_upgrade_message')
                    );
                    return null;
                }
            } catch (error) {
                // console.error("Error requesting document signature:", error);
                return null;
            }
        },

        async save_document(as_new = false) {
            // Check if user is registered
            if (this.user_data.account_status !== 'active') {
                this.show_premium_modal('registered_only');
                return;
            }

            try {
                // If as_new is true, always show save dialog
                if (as_new) {
                    await this.save_document_as();
                } else {
                    // Use direct save if we have a file handle, otherwise save as
                    await this.save_document_direct();
                }
                
                // Clear autosave after successful save
                this.clear_autosave();
            } catch (error) {
                // console.error("Error saving document:", error);
                this.show_error_message(this.get_t('document.save_error'), this.get_t('document.save_failed_message', { error: error.message }));
                this.status_message = this.get_t('status.error_saving');
            }
        },

        async save_document_direct() {
            // Check if user is registered
            if (this.user_data.account_status !== 'active') {
                this.show_premium_modal('registered_only');
                return;
            }

            // If File System Access API is available and we have a file handle
            if (this.supports_file_system_api && this.current_file_handle) {
                try {
                    this.status_message = this.get_t('status.saving');
                    
                    // Generate document data
                    const document_data = await this.serialize_document();
                    if (!document_data) {
                        this.status_message = this.get_t('status.save_failed_premium');
                        // The error popup is already shown by request_document_signature
                        return;
                    }
                    
                    // Encode with MessagePack
                    const binary_data = MessagePack.encode(document_data);
                    
                    // Write directly to the existing file
                    const writable = await this.current_file_handle.createWritable();
                    await writable.write(binary_data);
                    await writable.close();
                    
                    // Update status
                    this.document_has_unsaved_changes = false;
                    this.status_message = this.get_t('status.saved');
                    this.clear_autosave();
                    
                    // Add to recent files
                    this.add_to_recent_files(this.current_file_handle);
                    
                    return;
                    
                } catch (error) {
                    console.error('Direct save failed:', error);
                    // Fall through to save as
                }
            }
            
            // Special case: If we have original filename info but no file handle 
            // (e.g., loaded from autosave), inform user and fall through to save as
            if (this.autosave_original_filename && this.supports_file_system_api) {
                console.log(`No file handle available. Original file was: ${this.autosave_original_filename}`);
                console.log("User will need to select the file location again.");
                // Could optionally show a helpful message to user here
            }
            
            // Fallback: show save as dialog
            await this.save_document_as();
        },

        async save_document_as() {
            // Check if user is registered
            if (this.user_data.account_status !== 'active') {
                this.show_premium_modal('registered_only');
                return;
            }

            // Validate premium features BEFORE showing file dialog
            try {
                this.status_message = this.get_t('status.saving');
                const document_data = await this.serialize_document();
                if (!document_data) {
                    this.status_message = this.get_t('status.save_failed_premium');
                    // The error popup is already shown by request_document_signature
                    return;
                }
                
                // If validation passed, proceed with file dialog
                this.status_message = this.get_t('status.ready');
                
                // Try File System Access API first
                if (this.supports_file_system_api) {
                    try {
                        this.status_message = this.get_t('status.saving');
                        
                        // Show save file picker
                        const file_handle = await window.showSaveFilePicker({
                            suggestedName: `comic_${new Date().toISOString().replace(/[:.]/g, '-')}.mkc`,
                            types: [{
                                description: 'MiKai Comic files',
                                accept: { 'application/x-mkcomic': ['.mkc'] }
                            }]
                        });
                        
                        // Use the already validated document data
                        const binary_data = MessagePack.encode(document_data);
                        
                        // Write to file
                        const writable = await file_handle.createWritable();
                        await writable.write(binary_data);
                        await writable.close();
                        
                        // Store file handle for future direct saves
                        this.current_file_handle = file_handle;
                        this.current_file_path = file_handle.name;
                        this.document_has_unsaved_changes = false;
                        this.status_message = this.get_t('status.saved');
                        this.clear_autosave();
                        
                        // Add to recent files
                        this.add_to_recent_files(file_handle);
                        
                        return;
                              } catch (error) {
                    if (error.name !== 'AbortError') {
                        // console.error('File System API save failed:', error);
                        this.status_message = this.get_t('status.save_failed');
                        return;
                    } else {
                        // User cancelled
                        return;
                    }
                }
                }
                
                // Fallback for unsupported browsers: use the original save dialog
                await this.show_save_dialog();
                
            } catch (validationError) {
                // console.error('Premium validation failed during save as:', validationError);
                this.status_message = this.get_t('status.save_failed_premium');
                return;
            }
        },

        open_document() {
            // Check if user is registered
            if (this.user_data.account_status !== 'active') {
                this.show_premium_modal('registered_only');
                return;
            }

            // Check for unsaved changes
            this.check_unsaved_changes(() => {
                // Try File System Access API first
                if (this.supports_file_system_api) {
                    this.open_document_with_file_api();
                } else {
                    this.open_document_with_input();
                }
            });
        },

        async open_document_with_file_api() {
            try {
                // Show file picker
                const [file_handle] = await window.showOpenFilePicker({
                    types: [{
                        description: 'MiKai Comic files',
                        accept: { 'application/x-mkcomic': ['.mkc'] }
                    }]
                });
                
                // Get file and store handle for future saves
                const file = await file_handle.getFile();
                this.current_file_path = file_handle.name;
                
                // Load the document
                this.load_document_from_file(file);
                this.current_file_handle = file_handle;
                
                // Add to recent files
                this.add_to_recent_files(file_handle);
                
            } catch (error) {
                if (error.name !== 'AbortError') {
                    console.error('File System API open failed:', error);
                    // Fallback to input
                    this.open_document_with_input();
                }
            }
        },

        open_document_with_input() {
            // Create file input
            const file_input = document.createElement('input');
            file_input.type = 'file';
            file_input.accept = '.mkc,application/x-mkcomic';
            file_input.style.display = 'none';
            
            file_input.addEventListener('change', (e) => {
                const file = e.target.files[0];
                if (file) {
                    // Clear file handle since we used input
                    this.current_file_handle = null;
                    this.current_file_path = file.name;
                    this.load_document_from_file(file);
                }
                document.body.removeChild(file_input);
            });
            
            document.body.appendChild(file_input);
            file_input.click();
        },
  
        load_document_from_file(file) {
            this.status_message = "Loading...";
            
            const reader = new FileReader();
            
            reader.onload = async (e) => {
                try {
                    // Read as ArrayBuffer for binary processing
                    const buffer = e.target.result;
                    
                    // Decode MessagePack data using the correct API
                    let document_data;
                    try {
                        if (typeof MessagePack !== 'undefined') {
                            document_data = MessagePack.decode(new Uint8Array(buffer));
                        } else {
                            throw new Error("MessagePack library not available");
                        }
                    } catch (msgPackError) {
                        console.error("Error decoding MessagePack data:", msgPackError);
                        throw new Error("Failed to decode file format: " + msgPackError.message);
                    }
                    
                    // Validate basic structure - support both MKC1 and MKC2
                    if (!document_data || !document_data.header) {
                        throw new Error("Invalid file format: missing header");
                    }
                    
                    if (document_data.header !== "MKC1" && document_data.header !== "MKC2") {
                        throw new Error("Invalid file format: unsupported header " + document_data.header);
                    }
                    
                    // Extract components
                    const { metadata, signature, content } = document_data;
                    
                    // console.log("Opening document - verifying signature and premium permissions...");
                    // console.log("Document metadata:", { 
                    //     version: metadata.version,
                    //     header: document_data.header
                    // });
                    
                    // Verify signature with server
                    const validation = await this.verify_document_signature(metadata, signature);
                    
                    if (!validation || !validation.is_valid) {
                        // Clear file references to prevent accidental overwrite
                        this.current_file_handle = null;
                        this.current_file_path = null;
                        
                        this.show_error_message(
                            this.get_t('document.cannot_open_file'),
                            validation.message || this.get_t('document.file_tampered_or_premium')
                        );
                        this.status_message = this.get_t('status.validation_error');
                        return;
                    }
                    
                    // Get decryption key
                    const decryption_key = validation.decryption_key || signature;
                    
                    // Decrypt content with proper error handling
                    let decrypted_content;
                    try {
                        // First decrypt to WordArray
                        const decrypted_data = CryptoJS.AES.decrypt(content, decryption_key);
                        
                        // Check if decryption was successful
                        if (decrypted_data.sigBytes <= 0) {
                            throw new Error("Decryption failed - invalid key or corrupted data");
                        }
                        
                        // Try to convert to UTF-8 string
                        decrypted_content = decrypted_data.toString(CryptoJS.enc.Utf8);
                        
                        // Ensure we have valid data
                        if (!decrypted_content || decrypted_content.length === 0) {
                            throw new Error("Decryption produced empty result");
                        }
                    } catch (decryptError) {
                        console.error("Decryption error:", decryptError);
                        throw new Error("Failed to decrypt document: " + decryptError.message);
                    }
                    
                    // Parse decrypted JSON with error handling
                    let document_content;
                    try {
                        document_content = JSON.parse(decrypted_content);
                    } catch (parseError) {
                        console.error("JSON parse error:", parseError);
                        throw new Error("Failed to parse document structure");
                    }
                    
                    // Validate parsed content
                    if (!document_content) {
                        throw new Error("Invalid document structure: document content is null");
                    }
                    
                    // Check if this is MKC2 container data (format_version 2.0 with pages)
                    if (document_content.format_version === "2.0" && document_content.pages) {
                        // This is MKC2 container data, restore directly
                        this.restore_document_from_data(document_content);
                    } else if (document_content.document) {
                        // This is MKC1 individual page data (legacy, not supported)
                        throw new Error("Invalid file format: Application only supports MKC2 container files");
                    } else {
                        throw new Error("Invalid document structure: missing required properties");
                    }
                    
                    // Update state
                    this.current_file_path = file.name;
                    this.status_message = this.get_t('status.ready');
                    
                    // Mark as no unsaved changes after document is fully loaded
                    this.document_has_unsaved_changes = false;                    
                    
                    
                } catch (error) {
                    console.error("Error loading document:", error);
                    this.show_error_message(
                        this.get_t('document.error_loading_file'), 
                        this.get_t('document.file_corrupted_or_invalid', { error: error.message })
                    );
                    this.status_message = this.get_t('status.error_loading');
                }
            };
            
            // Read as binary
            reader.readAsArrayBuffer(file);

            this.hide_welcome_screen_view();
        },
  
        async verify_document_signature(metadata, signature) {
            const form_data = new FormData();
            form_data.append('request', "verify_document");
            form_data.append('metadata', JSON.stringify(metadata));
            form_data.append('signature', signature);
            
            try {
                const response = await axios.post('ajax.php', form_data, {
                    headers: {'Content-Type': 'multipart/form-data'}
                });
                
                return {
                    is_valid: response.data.status === "success",
                    message: response.data.message,
                    decryption_key: response.data.decryption_key
                };
            } catch (error) {
                console.error("Error verifying document signature:", error);
                return { 
                    is_valid: false, 
                    message: "Error de validación en el servidor. Inténtalo más tarde." 
                };
            }
        },

        async restore_document_from_data(document_content) {
            // Application only supports MKC2 containers - no retrocompatibility
            if (document_content.format_version !== "2.0" || !document_content.pages) {
                throw new Error("Invalid file format: Application only supports MKC2 container files");
            }
            
            // This is MKC2 container data
            this.container_data = document_content;
            this.is_mkc2_container = true;
            
            // Determine which page to open based on preferences
            let target_page_index = document_content.chapter_info?.current_page_index || 0;
            
            // Check if user preference is to open on last page
            if (this.preferences?.chapters?.open_last_page === true && this.container_data.pages.length > 0) {
                target_page_index = this.container_data.pages.length - 1;
            }
            
            // Ensure target page index is valid
            if (target_page_index >= this.container_data.pages.length) {
                target_page_index = this.container_data.pages.length - 1;
            }
            if (target_page_index < 0) {
                target_page_index = 0;
            }
            
            this.current_page_index = target_page_index;
            
            // Load target page data
            const target_page = document_content.pages[this.current_page_index];
            if (!target_page || !target_page.mkc_data) {
                throw new Error("Invalid container: No page data found");
            }
            
            // Load the current page (this is a file opening)
            await this.load_page_data(target_page.mkc_data, true);
            
            // Generate thumbnails for all pages after loading
            await this.generate_all_page_thumbnails();
        },

        async generate_all_page_thumbnails() {
            if (!this.is_mkc2_container || !this.container_data?.pages) {
                return;
            }
            
            const original_page_index = this.current_page_index;
            
            try {
                // Generate thumbnail for each page
                for (let i = 0; i < this.container_data.pages.length; i++) {
                    const page = this.container_data.pages[i];
                    
                    // Skip if page already has thumbnail
                    if (page.thumbnail) {
                        continue;
                    }
                    
                    // Skip if page has no data
                    if (!page.mkc_data) {
                        continue;
                    }
                    
                    // Switch to this page temporarily to generate thumbnail
                    if (i !== this.current_page_index) {
                        await this.perform_page_switch(i);
                    }
                    
                    // Generate and store thumbnail
                    const thumbnail = await this.generate_document_thumbnail_base64();
                    if (thumbnail) {
                        this.container_data.pages[i].thumbnail = thumbnail;
                    }
                }
                
                // Return to original page
                if (this.current_page_index !== original_page_index) {
                    await this.perform_page_switch(original_page_index);
                }
                
            } catch (error) {
                console.error("Error generating page thumbnails:", error);
                // Ensure we return to original page even if error occurs
                if (this.current_page_index !== original_page_index) {
                    try {
                        await this.perform_page_switch(original_page_index);
                    } catch (switchError) {
                        console.error("Error returning to original page:", switchError);
                    }
                }
            }
        },

        async load_page_data(document_content, is_file_opening = false) {
            // Validate document_content before proceeding
            if (!document_content || !document_content.document) {
                console.error('[ERROR] load_page_data: Invalid document_content provided:', document_content);
                this.show_error_message(this.get_t('error.title'), 'Invalid page data - the page may be corrupted');
                return;
            }
            
            // Extract document data
            const doc = document_content.document;
            if (!doc.canvas) {
                console.error('[ERROR] load_page_data: Missing canvas data in document');
                this.show_error_message(this.get_t('error.title'), 'Missing canvas data - the page may be corrupted');
                return;
            }
            
            const canvas_data = doc.canvas;
            
            // First clear current state
            this.deselect_all();
            
            // Only reset file handle if we're not in the middle of opening a file with File System API
            // The file handle should be preserved when opening files through the File System API
            if (!this.current_file_handle) {
                this.current_file_path = '';
            }
            
            // Clear autosave filename reference since we're loading a fresh document
            this.autosave_original_filename = null;
            
            // Update canvas configuration
            this.canvas_width = canvas_data.width || 800;
            this.canvas_height = canvas_data.height || 1200;
            this.current_format = canvas_data.format_id || 'comic';
            this.outline_width = canvas_data.outline_width || 2;
            this.outline_color = canvas_data.outline_color || '#000000';
            this.background_color = canvas_data.background_color || '#ffffff';
            this.page_margin = canvas_data.page_margin || 20;
            
            // Remove current panels
            this.panels.forEach(panel => panel.destroy());
            this.panels = [];
            this.panels_layer.destroyChildren();
            
            // Clear panel references to avoid using destroyed panels
            this.selected_panel = null;
            this.last_selected_panel = null;
            
            // Recreate background
            const background = new Konva.Rect({
                x: 0,
                y: 0,
                width: this.canvas_width,
                height: this.canvas_height,
                fill: this.background_color,
                name: 'background'
            });
            this.panels_layer.add(background);
            
            // Recreate panels
            if (doc.panels && doc.panels.length > 0) {
                // Create all panels first
                for (const panel_data of doc.panels) {
                    await this.recreate_panel_from_data(panel_data);
                }
            }
            
            // Update the view
            this.redraw();
            
            
            // Adjust zoom to visualize the entire document
            this.auto_fit_canvas_to_screen();
            this.hide_welcome_screen_view();

            await new Promise(resolve => {
                // Allow time for Konva to complete rendering
                setTimeout(() => {
                    this.select_global_layers();
                    
                    // Enable exterior stroke for loaded texts
                    if (typeof this.enable_exterior_stroke_for_existing_texts === 'function') {
                        this.enable_exterior_stroke_for_existing_texts();
                    }
                    
                    resolve();
                }, 300); 
            });
            
            // Clear history and set this state as initial (but don't restore - already loaded correctly)
            var snapshot = this.create_snapshot();
            this.history_stack = [snapshot];
            this.redo_stack = [];

            //this.restore_from_snapshot(snapshot);

            // Add to recent files ONLY when opening files, not when switching pages
            if (is_file_opening && this.current_file_path && typeof this.add_to_recent_files === 'function') {
                this.add_to_recent_files({
                    name: this.current_file_path,
                    handle: this.current_file_handle
                });
            }

            this.$nextTick(() => {
                this.activate_default_tool();
                // Ensure document is not marked as modified after loading
                this.document_has_unsaved_changes = false;
                
                // Generate missing thumbnails in background for MKC2 containers (DISABLED - was causing conflicts)
                if (false && this.is_mkc2_container) {
                    // Use setTimeout to not block UI
                    setTimeout(() => {
                        this.generate_missing_thumbnails();
                    }, 200);
                }
            });
        },

        async generate_missing_thumbnails() {
            if (!this.is_mkc2_container || !this.container_data?.pages) {
                return;
            }
            
            const original_page_index = this.current_page_index;
            
            try {
                // Generate thumbnails only for pages that don't have them
                for (let i = 0; i < this.container_data.pages.length; i++) {
                    const page = this.container_data.pages[i];
                    
                    // Skip if page already has thumbnail or no data
                    if (page.thumbnail || !page.mkc_data) {
                        continue;
                    }
                    
                    // Switch to this page temporarily to generate thumbnail
                    if (i !== this.current_page_index) {
                        await this.perform_page_switch(i);
                        await this.$nextTick(); // Wait for page to load
                        await new Promise(resolve => setTimeout(resolve, 100)); // Small delay for rendering
                    }
                    
                    // Generate and store thumbnail
                    const thumbnail = await this.generate_document_thumbnail_base64();
                    if (thumbnail) {
                        this.container_data.pages[i].thumbnail = thumbnail;
                    }
                }
                
                // Return to original page
                if (this.current_page_index !== original_page_index) {
                    await this.perform_page_switch(original_page_index);
                }
                
            } catch (error) {
                console.error("Error generating missing thumbnails:", error);
                // Ensure we return to original page even if error occurs
                if (this.current_page_index !== original_page_index) {
                    try {
                        await this.perform_page_switch(original_page_index);
                    } catch (switchError) {
                        console.error("Error returning to original page:", switchError);
                    }
                }
            }
        },

        async load_document_from_handle(file_handle) {
            try {
                // Check if handle has permission
                const permission = await file_handle.queryPermission({ mode: 'read' });
                if (permission !== 'granted') {
                    const request_permission = await file_handle.requestPermission({ mode: 'read' });
                    if (request_permission !== 'granted') {
                        throw new Error('Permission denied');
                    }
                }
                
                // Get file from handle
                const file = await file_handle.getFile();
                this.current_file_handle = file_handle;
                this.current_file_path = file_handle.name;
                
                // Load the document using existing method
                this.load_document_from_file(file);
                
                // Ensure document is not marked as modified after loading
                this.$nextTick(() => {
                    this.document_has_unsaved_changes = false;
                });
                
            } catch (error) {
                console.error('Error loading document from handle:', error);
                this.status_message = this.get_t('status.error_loading_document');
                throw error;
            }
        },

        init_indexed_db() {
            const request = indexedDB.open('MikaiComicEditor', 2);
            
            request.onupgradeneeded = (event) => {
                const db = event.target.result;
                if (!db.objectStoreNames.contains('autosaves')) {
                    db.createObjectStore('autosaves', { keyPath: 'id' });
                }
                if (!db.objectStoreNames.contains('file_handles')) {
                    db.createObjectStore('file_handles', { keyPath: 'name' });
                }
            };
            
            request.onsuccess = (event) => {
                this.db = event.target.result;
            };
            
            request.onerror = (event) => {
                console.error("IndexedDB error:", event.target.error);
                this.status_message = this.get_t('status.error_initializing_autosave');
            };
        },

        start_autosave_timer() {
            // Clear any existing interval to avoid duplicates
            if (this.autosave_interval) {
                clearInterval(this.autosave_interval);
            }
            
            // Set new interval based directly on preferences
            if (this.preferences.editor.autosave_enabled) {
                // Convert minutes to milliseconds
                const autosave_frequency = parseInt(this.preferences.editor.autosave_interval) * 60 * 1000;
                
                this.autosave_interval = setInterval(() => {
                    this.perform_autosave();
                }, autosave_frequency);
                
                //console.log("Autosave timer started - " + this.preferences.editor.autosave_interval + " minutes");
            } else {
                //console.log("Autosave disabled");
            }
        },

        async perform_autosave() {
            // Only registered users can use autosave
            if (this.user_data.account_status !== 'active') {
                return;
            }

            // Only save if there are unsaved changes and DB is initialized
            if (!this.db || !this.document_has_unsaved_changes) return;
            
            // Only perform save if this is the visible/active tab - simple check
            if (document.visibilityState !== 'visible') {
                console.log("Tab not active, skipping autosave");
                return;
            }
            
            // If document has no unsaved changes, clear any existing autosave and return
            if (!this.document_has_unsaved_changes) {
                console.log("No unsaved changes, clearing autosave");
                this.clear_autosave();
                return;
            }
            
            try {
                // Generate the same MKC document we use for file saving, but with autosave flag
                const mkc_document = await this.serialize_document(null, true);
                const timestamp = Date.now();
                
                // Store in IndexedDB
                const transaction = this.db.transaction(['autosaves'], 'readwrite');
                const store = transaction.objectStore('autosaves');
                
                // Convert to string to avoid cloning issues
                const autosave_data = {
                    id: 'current_autosave',
                    timestamp: timestamp,
                    mkc_document_string: JSON.stringify(mkc_document),
                    file_path: this.current_file_path,
                    // Store File System API reference if available
                    has_file_handle: !!this.current_file_handle,
                    file_handle_name: this.current_file_handle ? this.current_file_handle.name : null
                };
                
                store.put(autosave_data);
                
                transaction.oncomplete = () => {
                    console.log("Autosave completed at " + new Date(timestamp).toLocaleTimeString());
                    // Update status message briefly
                    const original_message = this.status_message;
                    this.status_message = "Autosaved";
                    setTimeout(() => {
                        if (this.status_message === "Autosaved") {
                            this.status_message = original_message;
                        }
                    }, 2000);
                };
            } catch (error) {
                console.error("Autosave error:", error);
            }
        },

        check_pending_autosave() {
            // Wait for both DB and language to be loaded before showing popup
            if (!this.db || this.language_loading || Object.keys(this.lang).length === 0) {
                // Retry after DB or language is initialized
                setTimeout(() => this.check_pending_autosave(), 200);
                return;
            }
            
            const transaction = this.db.transaction(['autosaves'], 'readonly');
            const store = transaction.objectStore('autosaves');
            const request = store.get('current_autosave');
            
            request.onsuccess = (event) => {
                const autosave = event.target.result;
                if (autosave) {
                    // Calculate age of autosave
                    const age_ms = Date.now() - autosave.timestamp;
                    const age_minutes = Math.floor(age_ms / 60000);
                    
                    // Show recovery popup
                    const recover_html = `
                        <div class="text-center space-y-4">
                            <p class="text-lg font-medium text-gray-100">${this.get_t('document.autosave_found_message', { minutes: age_minutes })}</p>
                        </div>
                        <div class="flex justify-center gap-4 mt-8">
                            <button id="recover_confirm" class="px-6 py-3 bg-gradient-to-r from-rose-500 to-rose-600 text-white rounded-xl hover:from-rose-400 hover:to-rose-500 transition-all duration-200 font-medium shadow-lg shadow-rose-600/25">
                                ${this.get_t('document.recover')}
                            </button>
                            <button id="recover_cancel" class="px-6 py-3 bg-gray-700/50 text-white rounded-xl hover:bg-gray-600/50 transition-all duration-200 border border-gray-600/30 font-medium">
                                ${this.get_t('document.discard')}
                            </button>
                        </div>
                    `;
                    
                    this.show_popup_with_content(this.get_t('document.recover_autosave'), recover_html, { type: 'info' });
                    
                    this.$nextTick(() => {
                        this.refresh_lucide();
                        const confirm_btn = document.getElementById('recover_confirm');
                        const cancel_btn = document.getElementById('recover_cancel');
                        
                        if (confirm_btn && cancel_btn) {
                            confirm_btn.onclick = () => {
                                // Check if user is logged in before allowing autosave recovery
                                if (!this.user_data || !this.user_data.email) {
                                    this.pending_autosave_recovery = autosave;
                                    this.show_login_modal();
                                    return;
                                }
                                this.restore_from_autosave(autosave);
                                this.close_popup();
                            };
                            cancel_btn.onclick = () => {
                                this.clear_autosave();
                                this.close_popup();
                            };
                        }
                    });
                }
            };
        },

        async restore_from_autosave(autosave) {
            // Check if user is registered
            if (this.user_data.account_status !== 'active') {
                this.show_error_message(
                    this.get_t('document.cannot_open_file'),
                    this.get_t('auth.registered_users_only')
                );
                return;
            }

            try {
                // Parse the MKC document from the stored JSON string
                const mkc_document = autosave.mkc_document_string ? 
                    JSON.parse(autosave.mkc_document_string) : null;
                
                if (!mkc_document) {
                    throw new Error("Invalid autosave data");
                }
                
                // Extract components
                const { metadata, signature, content } = mkc_document;
                
                // console.log("Restoring autosave - verifying document signature and premium permissions...");
                // console.log("Document metadata:", { 
                //     panels: metadata.panels_count, 
                //     balloons: metadata.balloons_count, 
                //     max_layers: metadata.max_layers_per_panel || 'N/A (legacy)' 
                // });
                
                // Verify signature with server (includes premium validation)
                const validation = await this.verify_document_signature(metadata, signature);
                
                if (!validation || !validation.is_valid) {
                    console.log("Autosave recovery blocked:", validation.message);
                    this.show_error_message(
                        this.get_t('document.cannot_open_file'),
                        validation.message || this.get_t('document.file_tampered_or_premium')
                    );
                    this.status_message = this.get_t('status.validation_error');
                    return;
                }
                
                console.log("Autosave recovery successful - premium validation passed");
                
                // Get decryption key
                const decryption_key = validation.decryption_key || signature;
                
                try {
                    // Decrypt content with proper error handling
                    const decrypted_data = CryptoJS.AES.decrypt(content, decryption_key.toString());
                    
                    if (decrypted_data.sigBytes <= 0) {
                        throw new Error("Decryption failed");
                    }
                    
                    // Convert to UTF-8 string
                    const decrypted_string = decrypted_data.toString(CryptoJS.enc.Utf8);
                    if (!decrypted_string) {
                        throw new Error("Decryption produced empty result");
                    }
                    
                    // Parse JSON
                    const document_content = JSON.parse(decrypted_string);
                    
                    // Restore document
                    this.restore_document_from_data(document_content);
                    
                    // Restore file information from autosave
                    if (autosave.file_path) {
                        this.current_file_path = autosave.file_path;
                    }
                    
                    // If this autosave came from a file opened with File System API,
                    // try to restore the file handle from IndexedDB
                    if (autosave.has_file_handle && autosave.file_handle_name && this.supports_file_system_api) {
                        // Store the original filename for user reference
                        this.autosave_original_filename = autosave.file_handle_name;
                        console.log(`Autosave was from File System API file: ${autosave.file_handle_name}`);
                        
                        // Try to restore the file handle from IndexedDB
                        try {
                            const restored_handle = await this.get_file_handle(autosave.file_handle_name);
                            if (restored_handle && typeof restored_handle.getFile === 'function') {
                                this.current_file_handle = restored_handle;
                                console.log(`File handle successfully restored for: ${autosave.file_handle_name}`);
                            } else {
                                console.log("File handle lost - user will need to use Save As or reopen file");
                            }
                        } catch (error) {
                            console.error('Error restoring file handle:', error);
                            console.log("File handle lost - user will need to use Save As or reopen file");
                        }
                    }
                    
                    // Mark as no unsaved changes when opening from autosave
                    this.document_has_unsaved_changes = false;
                    this.status_message = this.get_t('status.ready');
                    
                    // Hide welcome screen after successful autosave recovery
                    this.hide_welcome_screen_view();


                } catch (error) {
                    console.error("Error processing autosave:", error);
                    this.show_error_message(
                        this.get_t('document.autosave_recovery_failed'), 
                        this.get_t('document.could_not_restore_autosave')
                    );
                }
            } catch (error) {
                console.error("Autosave recovery error:", error);
                this.show_error_message(
                    this.get_t('document.autosave_error'), 
                    this.get_t('document.failed_to_restore_autosave', { error: error.message })
                );
            }
        },

        clear_autosave() {
            if (!this.db) return;
            
            const transaction = this.db.transaction(['autosaves'], 'readwrite');
            const store = transaction.objectStore('autosaves');
            store.delete('current_autosave');
            
            transaction.oncomplete = () => {
                console.log("Autosave cleared");
            };
        },

        confirm_new_document() {
            this.show_file_menu = false; // Close menu when action is selected
    
            // Use the standard check_unsaved_changes method for consistency
            this.check_unsaved_changes(() => {
                // Exit all edit modes before creating new document
                this.exit_balloon_edit_mode();
                
                // When confirmed or no changes to save, show the new document dialog
                this.show_new_document_dialog();
            });

        },

        close_document() {
            // Close the file menu when action is selected
            this.show_file_menu = false;
            
            // Use the standard check_unsaved_changes method for consistency
            this.check_unsaved_changes(() => {
                // Exit all edit modes before clearing canvas
                this.exit_balloon_edit_mode();
                
                // Clear the canvas first
                this.reset_layout(true);
                
                // Show the welcome screen
                this.show_welcome_screen = true;
                
                // Reset document state
                this.document_has_unsaved_changes = false;
                this.current_file_handle = null;
                this.current_file_path = null;
                this.is_mkc2_container = false;
                this.container_data = null;
                this.current_page_index = 0;
                
                // Clear autosave
                this.clear_autosave();
            });
        },

        check_unsaved_changes(callback) {
            // Check if there's unsaved work
            if (this.document_has_unsaved_changes) {
                // Show warning popup with clearer message
                const confirm_html = `
                    <div class="text-center space-y-4">
                        <p class="text-lg font-medium text-gray-100">${this.get_t('document.unsaved_changes_message')}</p>
                    </div>
                    <div class="flex justify-center gap-4 mt-8">
                        <button id="unsaved_confirm" class="px-6 py-3 bg-gradient-to-r from-rose-500 to-rose-600 text-white rounded-xl hover:from-rose-400 hover:to-rose-500 transition-all duration-200 font-medium shadow-lg shadow-rose-600/25">
                            ${this.get_t('document.continue_without_saving')}
                        </button>
                        <button id="unsaved_cancel" class="px-6 py-3 bg-gray-700/50 text-white rounded-xl hover:bg-gray-600/50 transition-all duration-200 border border-gray-600/30 font-medium">
                            ${this.get_t('actions.cancel')}
                        </button>
                    </div>
                `;
                
                this.show_popup_with_content(this.get_t('document.unsaved_changes_title'), confirm_html, { type: 'warning' });
                
                this.$nextTick(() => {
                    this.refresh_lucide();
                    const confirm_btn = document.getElementById('unsaved_confirm');
                    const cancel_btn = document.getElementById('unsaved_cancel');
                    
                    if (confirm_btn && cancel_btn) {
                        confirm_btn.onclick = () => {
                            this.close_popup();
                            callback();
                        };
                        cancel_btn.onclick = () => this.close_popup();
                    }
                });
            } else {
                // No unsaved changes, proceed immediately
                callback();
            }
        },

        //! Not only a UI function, but also a core functionality
        async show_save_dialog() {
            try {
                // Validate premium features BEFORE showing dialog
                this.status_message = this.get_t('status.saving');
                const document_data = await this.serialize_document();
                if (!document_data) {
                    this.status_message = this.get_t('status.save_failed_premium');
                    // The error popup is already shown by request_document_signature
                    return;
                }
                
                // If validation passed, proceed with filename dialog
                this.status_message = this.get_t('status.ready');

                // First, show a custom dialog to get the filename from the user
                const default_filename = `comic_${new Date().toISOString().replace(/[:.]/g, '-')}.mkc`;
                
                // Create the dialog HTML with an input field
                const dialog_html = `
                    <div class="space-y-4">
                        <div>
                            <label class="block text-sm font-medium text-gray-300 mb-2">${this.get_t('document.file_name')}:</label>
                            <input id="filename_input" type="text" value="${default_filename}" 
                                class="w-full bg-gray-700 border border-gray-600 rounded-lg p-3 text-white placeholder-gray-400 focus:border-rose-500 focus:ring-1 focus:ring-rose-500 transition-colors duration-200">
                        </div>
                    </div>
                    <div class="flex justify-center gap-4 mt-8">
                        <button id="save_confirm" class="px-6 py-3 bg-rose-600 text-white rounded-lg hover:bg-rose-700 transition-colors duration-200 font-medium">
                            ${this.get_t('document.save')}
                        </button>
                        <button id="save_cancel" class="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors duration-200 font-medium">
                            ${this.get_t('actions.cancel')}
                        </button>
                    </div>
                `;
                
                this.show_popup_with_content(this.get_t('document.save_file'), dialog_html, { type: 'info' });
                
                // Wait for user input using a promise
                const user_filename = await new Promise((resolve, reject) => {
                    this.$nextTick(() => {
                        const save_btn = document.getElementById('save_confirm');
                        const cancel_btn = document.getElementById('save_cancel');
                        const input = document.getElementById('filename_input');
                        
                        // Auto-focus and select text in input
                        if (input) {
                            input.focus();
                            input.select();
                            
                            // Add Enter key handler
                            input.addEventListener('keydown', (e) => {
                                if (e.key === 'Enter') {
                                    save_btn.click();
                                }
                            });
                        }
                        
                        if (save_btn && cancel_btn) {
                            save_btn.onclick = () => {
                                const filename = input.value.trim();
                                // Add .mkc extension if not present
                                const final_filename = filename.endsWith('.mkc') ? filename : `${filename}.mkc`;
                                this.close_popup();
                                resolve(final_filename);
                            };
                            
                            cancel_btn.onclick = () => {
                                this.close_popup();
                                reject(new Error('Save cancelled'));
                            };
                        }
                    });
                });
                
                // If the user cancels, stop the process
                if (!user_filename) return;
                
                // Show saving status
                this.status_message = "Saving...";
                
                // Use the already validated document data (no need to serialize again)
                
                // Use MessagePack API correctly
                let binary_data;
                if (typeof MessagePack !== 'undefined') {
                    binary_data = MessagePack.encode(document_data);
                } else {
                    throw new Error("MessagePack library not available");
                }
                
                // Create blob with custom MIME type
                const blob = new Blob([binary_data], { type: 'application/x-mkcomic' });
                
                // Create link for download using the user-specified filename
                const a = document.createElement('a');
                a.href = URL.createObjectURL(blob);
                a.download = user_filename;
                a.style.display = 'none';
                document.body.appendChild(a);
                a.click();
                
                // Clean up
                setTimeout(() => {
                    document.body.removeChild(a);
                    URL.revokeObjectURL(a.href);
                }, 100);
                
                // Save reference to current filename - with the user-defined name
                this.current_file_path = user_filename;
                this.document_has_unsaved_changes = false;
                this.status_message = "Saved";
                
                // Clear autosave if implemented
                if (this.clear_autosave) {
                    this.clear_autosave();
                }
                
            } catch (error) {
                console.error("Error in save dialog:", error);
                this.status_message = "Save failed";
                
                // Show error to user
                this.show_error_message("Save Error", 
                    "Failed to save document: " + error.message);
            }
        },

        // Export functions
        show_export_modal() {
            this.show_export_dialog = true;
            this.close_all_menus();
        },

        close_export_modal() {
            this.show_export_dialog = false;
            this.export_processing = false;
        },

        calculate_final_resolution() {
            const base_width = this.canvas_width;
            const base_height = this.canvas_height;
            const pixel_ratio = this.export_pixel_ratio;
            
            return {
                width: base_width * pixel_ratio,
                height: base_height * pixel_ratio
            };
        },

        async export_canvas_image() {
            // Check if user is registered
            if (this.user_data.account_status !== 'active') {
                this.show_premium_modal('registered_only');
                return;
            }

            this.export_processing = true;
            
            try {
                // Check export resolution before server validation
                const export_resolution = this.calculated_final_resolution;
                
                // Check absolute maximum dimensions (10000x10000) - no tier can exceed this
                const max_dimension = Math.max(export_resolution.width, export_resolution.height);
                if (max_dimension > 10000) {
                    this.export_processing = false;
                    this.show_error_message(
                        this.get_t('error.title'),
                        this.get_t('premium.dimension_limit_exceeded')
                    );
                    return;
                }
                
                // Check premium limits with server validation (including document content)
                
                // Generate current document metadata for validation
                const snapshot = this.create_snapshot();
                const regular_panels = snapshot.panels_data.filter(panel => panel && !panel.is_global);
                const total_balloons = regular_panels.reduce((count, panel) => {
                    return count + (panel.content ? panel.content.filter(obj => obj.object_type === 'balloon').length : 0);
                }, 0);
                const max_layers_per_panel = regular_panels.reduce((max, panel) => {
                    const layer_count = panel.content ? panel.content.length : 0;
                    return Math.max(max, layer_count);
                }, 0);
                
                const current_document_metadata = {
                    panels_count: regular_panels.length,
                    balloons_count: total_balloons,
                    max_layers_per_panel: max_layers_per_panel
                };
                
                const is_allowed = await this.check_premium_limit('high_res_export', {
                    width: export_resolution.width,
                    height: export_resolution.height,
                    pixel_ratio: this.export_pixel_ratio,
                    format: this.export_format,
                    document_metadata: current_document_metadata
                });
                
                if (!is_allowed) {
                    this.export_processing = false;
                    return;
                }
                
                // Server validated the export, use current settings
                const final_pixel_ratio = this.export_pixel_ratio;
                const final_format = this.export_format;
                
                console.log('Export approved, using settings:', {
                    pixel_ratio: final_pixel_ratio,
                    format: final_format,
                    width: this.canvas_width,
                    height: this.canvas_height
                });
                
                // Hide UI layer during export
                this.ui_layer.visible(false);
                
                // Store current stage transform
                const original_x = this.stage.x();
                const original_y = this.stage.y();
                const original_scale_x = this.stage.scaleX();
                const original_scale_y = this.stage.scaleY();
                
                // Reset stage transform to export clean canvas
                this.stage.position({ x: 0, y: 0 });
                this.stage.scale({ x: 1, y: 1 });
                
                // Configure export options
                const mime_type = this.get_mime_type(final_format);
                const export_options = {
                    mimeType: mime_type,
                    pixelRatio: final_pixel_ratio,
                    width: this.canvas_width,
                    height: this.canvas_height,
                    x: 0,
                    y: 0
                };

                // Add quality for lossy formats
                if (final_format === 'jpeg') {
                    export_options.quality = this.export_quality;
                } 
                else if (final_format === 'webp') {
                    // WebP quality handling - ensure proper range and fallback to lossless if needed
                    if (this.export_quality >= 0.95) {
                        // Use lossless WebP for highest quality
                        export_options.quality = 1.0;
                    } else {
                        // Ensure quality is within valid range for lossy WebP
                        export_options.quality = Math.max(0.1, Math.min(0.9, this.export_quality));
                    }
                }

                // Generate the image data
                const data_url = this.stage.toDataURL(export_options);
                
                // Restore original stage transform
                this.stage.position({ x: original_x, y: original_y });
                this.stage.scale({ x: original_scale_x, y: original_scale_y });
                
                // Generate filename based on current file or auto-generate if never saved
                let filename;
                if (this.current_file_path || this.autosave_original_filename) {
                    // Extract filename from path and remove .mkc extension
                    let full_path = this.current_file_path || this.autosave_original_filename;
                    let file_name = full_path.split('/').pop().split('\\').pop(); // Get just the filename from path
                    let base_name = file_name.replace(/\.mkc$/i, ''); // Remove .mkc extension (case insensitive)
                    
                    // Ensure we have a valid base name (handle files that start with dot)
                    if (!base_name || base_name === '.') {
                        base_name = 'untitled';
                    }
                    
                    // For MKC2 containers, include page information in filename
                    if (this.is_mkc2_container && this.container_data && this.container_data.pages && this.container_data.pages.length > 1) {
                        const page_number = this.current_page_index + 1; // Convert to 1-based numbering
                        filename = `${base_name}_page_${page_number}.${final_format}`;
                    } else {
                        filename = `${base_name}.${final_format}`;
                    }
                } else {
                    // Auto-generate filename for unsaved files
                    const final_resolution = this.calculated_final_resolution;
                    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').split('T')[0];
                    
                    // For MKC2 containers, include page information in filename
                    if (this.is_mkc2_container && this.container_data && this.container_data.pages && this.container_data.pages.length > 1) {
                        const page_number = this.current_page_index + 1; // Convert to 1-based numbering
                        filename = `comic-${timestamp}-${final_resolution.width}x${final_resolution.height}_page_${page_number}.${final_format}`;
                    } else {
                        filename = `comic-${timestamp}-${final_resolution.width}x${final_resolution.height}.${final_format}`;
                    }
                }
                
                const link = document.createElement('a');
                link.download = filename;
                link.href = data_url;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                
                // Close modal
                this.close_export_modal();
                
            } catch (error) {
                // console.error('Export failed:', error);
                this.show_error_message(
                    this.get_t('export.error_title'),
                    this.get_t('export.error_message') + ': ' + error.message
                );
            } finally {
                // Restore UI layer visibility
                this.ui_layer.visible(true);
                this.ui_layer.batchDraw();
                this.export_processing = false;
            }
        },

        get_mime_type(format) {
            const mime_types = {
                'png': 'image/png',
                'jpeg': 'image/jpeg', 
                'webp': 'image/webp'
            };
            return mime_types[format] || 'image/png';
        },


        //! Recent files management
        async load_recent_files() {
            try {
                const stored_files = localStorage.getItem('mikomic_recent_files');
                this.recent_files = stored_files ? JSON.parse(stored_files) : [];
                
                // Load thumbnails for recent files
                await this.load_recent_files_thumbnails();
            } catch (error) {
                console.error('Error loading recent files:', error);
                this.recent_files = [];
            }
        },

        async load_recent_files_thumbnails() {
            if (this.recent_files.length === 0) return;
            
            for (let file of this.recent_files) {
                if (!file.thumbnail && file.handle) {
                    try {
                        // Read file handle and extract thumbnail
                        const file_obj = await file.handle.getFile();
                        const arrayBuffer = await file_obj.arrayBuffer();
                        
                        // Decode MKC file using MessagePack
                        let mkc_data = null;
                        try {
                            if (typeof MessagePack !== 'undefined') {
                                mkc_data = MessagePack.decode(new Uint8Array(arrayBuffer));
                            }
                        } catch (decode_error) {
                            console.log(`Could not decode ${file.name}:`, decode_error);
                        }
                        
                        // Extract thumbnail if found
                        if (mkc_data && mkc_data.metadata && mkc_data.metadata.document_thumbnail) {
                            file.thumbnail = `data:image/png;base64,${mkc_data.metadata.document_thumbnail}`;
                        }
                    } catch (error) {
                        console.log(`Could not load thumbnail for ${file.name}:`, error);
                    }
                }
            }
        },

        save_recent_files() {
            try {
                // Store file handles in IndexedDB and metadata in localStorage
                const serializable_files = this.recent_files.map(file => ({
                    name: file.name,
                    last_opened: file.last_opened,
                    has_handle: !!file.handle,
                    thumbnail: file.thumbnail || null
                }));
                localStorage.setItem('mikomic_recent_files', JSON.stringify(serializable_files));
                
                // Store file handles in IndexedDB
                if (this.db) {
                    this.recent_files.forEach(file => {
                        if (file.handle) {
                            this.store_file_handle(file.name, file.handle);
                        }
                    });
                }
            } catch (error) {
                console.error('Error saving recent files:', error);
            }
        },

        async add_to_recent_files(file_info) {
            // Remove if already exists
            this.recent_files = this.recent_files.filter(f => f.name !== file_info.name);
            
            // Generate thumbnail for current document if not provided
            let thumbnail = file_info.thumbnail;
            if (!thumbnail && this.stage && this.stage.children.length > 0) {
                // For MKC2 containers, ALWAYS use page 1 thumbnail from container data
                if (this.is_mkc2_container && this.container_data && this.container_data.pages && this.container_data.pages.length > 0 && this.container_data.pages[0].thumbnail) {
                    thumbnail = `data:image/png;base64,${this.container_data.pages[0].thumbnail}`;
                } else {
                    // Add delay to ensure document is fully loaded and rendered
                    await new Promise(resolve => setTimeout(resolve, 300));
                    
                    // Verify stage has content after delay
                    if (this.stage && this.stage.children.length > 0) {
                        thumbnail = await this.generate_document_thumbnail_base64();
                        if (thumbnail) {
                            thumbnail = `data:image/png;base64,${thumbnail}`;
                        }
                    }
                }
            }
            
            // Add to beginning
            this.recent_files.unshift({
                name: file_info.name,
                last_opened: new Date().toISOString(),
                handle: file_info.handle || null,
                thumbnail: thumbnail || null
            });
            
            // Keep only last 10 files
            this.recent_files = this.recent_files.slice(0, 10);
            
            this.save_recent_files();
        },

        remove_from_recent_files(file_name) {
            // Remove file from recent files list
            this.recent_files = this.recent_files.filter(f => f.name !== file_name);
            this.save_recent_files();
            
            // Also remove from IndexedDB if exists
            if (this.db) {
                try {
                    const transaction = this.db.transaction(['file_handles'], 'readwrite');
                    const store = transaction.objectStore('file_handles');
                    store.delete(file_name);
                } catch (error) {
                    console.error('Error removing file handle from IndexedDB:', error);
                }
            }
        },
        
        async store_file_handle(file_name, file_handle) {
            if (!this.db) return;
            try {
                const transaction = this.db.transaction(['file_handles'], 'readwrite');
                const store = transaction.objectStore('file_handles');
                store.put({ name: file_name, handle: file_handle });
            } catch (error) {
                console.error('Error storing file handle:', error);
            }
        },
        
        async get_file_handle(file_name) {
            if (!this.db) return null;
            try {
                const transaction = this.db.transaction(['file_handles'], 'readonly');
                const store = transaction.objectStore('file_handles');
                const request = store.get(file_name);
                
                return new Promise((resolve) => {
                    request.onsuccess = () => {
                        resolve(request.result ? request.result.handle : null);
                    };
                    request.onerror = () => {
                        resolve(null);
                    };
                });
            } catch (error) {
                console.error('Error getting file handle:', error);
                return null;
            }
        },
        
        open_from_recent_with_auth_check(file_info) {
            // Check if user is registered
            if (this.user_data.account_status !== 'active') {
                this.show_premium_modal('registered_only');
                return;
            }
            
            this.open_from_recent(file_info);
        },

        // Container page management functions
        async switch_to_page(page_index) {
            if (!this.is_mkc2_container || !this.container_data || page_index < 0 || page_index >= this.container_data.pages.length) {
                return;
            }
            
            if (page_index === this.current_page_index) {
                return; // Already on this page
            }
            
            if (this.is_switching_pages) {
                return; // Already switching pages
            }
            
            // Set flag to prevent multiple simultaneous switches
            this.is_switching_pages = true;
            
            try {
                // Check for unsaved changes
                if (this.document_has_unsaved_changes) {
                    // Show unsaved changes modal for page switching
                    this.show_page_switch_unsaved_modal(page_index);
                    return;
                }
                
                // Switch to page
                await this.perform_page_switch(page_index);
            } finally {
                // Always clear the flag
                this.is_switching_pages = false;
            }
        },

        async perform_page_switch(page_index, save_current_page = true) {
            // Save current page data to container only if requested
            if (save_current_page && this.is_mkc2_container && this.container_data) {
                const current_snapshot = this.create_snapshot();
                const document_data = {
                    format_version: "1.0",
                    created_at: this.container_data.pages[this.current_page_index].mkc_data.created_at,
                    last_modified: new Date().toISOString(),
                    document: {
                        canvas: {
                            width: current_snapshot.canvas_width,
                            height: current_snapshot.canvas_height,
                            background_color: current_snapshot.background_color,
                            outline_color: current_snapshot.outline_color,
                            outline_width: current_snapshot.outline_width,
                            page_margin: current_snapshot.page_margin,
                            format_id: this.current_format
                        },
                        panels: current_snapshot.panels_data
                    }
                };
                
                this.container_data.pages[this.current_page_index].mkc_data = document_data;
                this.container_data.pages[this.current_page_index].page_last_modified = new Date().toISOString();
                
                // Generate and update thumbnail for current page (always update when switching)
                const thumbnail = await this.generate_document_thumbnail_base64();
                if (thumbnail) {
                    this.container_data.pages[this.current_page_index].thumbnail = thumbnail;
                }
            }
            
            // Update current page index
            this.current_page_index = page_index;
            
            // Load target page (this is NOT a file opening, just page switching)
            const target_page = this.container_data.pages[page_index];
            
            // Validate target page has valid mkc_data
            if (!target_page || !target_page.mkc_data) {
                console.error('[ERROR] perform_page_switch: Target page has no valid mkc_data:', target_page);
                this.show_error_message(this.get_t('error.title'), `Page ${page_index + 1} data is corrupted or missing`);
                return;
            }
            
            await this.load_page_data(target_page.mkc_data, false);
            
            // Clear unsaved changes flag
            this.document_has_unsaved_changes = false;
        },

        show_page_switch_unsaved_modal(target_page_index) {
            const is_creating_new_page = target_page_index === 'new_page';
            
            const confirm_html = `
                <div class="text-center space-y-4">
                    <p class="text-lg font-medium text-gray-100">${this.get_t('document.unsaved_page_changes_message')}</p>
                    <p class="text-sm text-gray-300">${this.get_t('document.page_switch_explanation')}</p>
                </div>
                <div class="flex justify-center gap-4 mt-8">
                    <button id="page_switch_save" class="px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl hover:from-blue-400 hover:to-blue-500 transition-all duration-200 font-medium shadow-lg shadow-blue-600/25" title="${this.get_t('document.save_complete_file_tooltip')}">
                        ${this.get_t('document.save_complete_file')}
                    </button>
                    <button id="page_switch_discard" class="px-6 py-3 bg-gradient-to-r from-rose-500 to-rose-600 text-white rounded-xl hover:from-rose-400 hover:to-rose-500 transition-all duration-200 font-medium shadow-lg shadow-rose-600/25" title="${this.get_t('document.discard_page_changes_tooltip')}">
                        ${this.get_t('document.discard_page_changes')}
                    </button>
                    <button id="page_switch_cancel" class="px-6 py-3 bg-gray-700/50 text-white rounded-xl hover:bg-gray-600/50 transition-all duration-200 border border-gray-600/30 font-medium">
                        ${this.get_t('actions.cancel')}
                    </button>
                </div>
            `;
            
            this.show_popup_with_content(this.get_t('document.unsaved_changes_title'), confirm_html, { type: 'warning' });
            
            this.$nextTick(() => {
                this.refresh_lucide();
                const save_btn = document.getElementById('page_switch_save');
                const discard_btn = document.getElementById('page_switch_discard');
                const cancel_btn = document.getElementById('page_switch_cancel');
                
                if (save_btn && discard_btn && cancel_btn) {
                    save_btn.onclick = async () => {
                        this.close_popup();
                        try {
                            await this.save_document();
                            
                            if (is_creating_new_page) {
                                await this.validate_and_create_new_page();
                            } else {
                                await this.perform_page_switch(target_page_index);
                            }
                        } catch (error) {
                            console.error('Error saving before action:', error);
                        } finally {
                            this.is_switching_pages = false;
                        }
                    };
                    
                    discard_btn.onclick = async () => {
                        this.close_popup();
                        
                        try {
                            // Restore current page to its saved state before action
                            if (this.is_mkc2_container && this.container_data) {
                                const current_page_data = this.container_data.pages[this.current_page_index];
                                if (current_page_data && current_page_data.mkc_data) {
                                    // Reload current page from container to discard changes (NOT a file opening)
                                    await this.load_page_data(current_page_data.mkc_data, false);
                                }
                            }
                            
                            // Clear unsaved changes flag and proceed with action
                            this.document_has_unsaved_changes = false;
                            
                            if (is_creating_new_page) {
                                await this.validate_and_create_new_page();
                            } else {
                                await this.perform_page_switch(target_page_index, false); // Don't save current page
                            }
                        } finally {
                            this.is_switching_pages = false;
                        }
                    };
                    
                    cancel_btn.onclick = () => {
                        this.close_popup();
                        this.is_switching_pages = false; // Clear flag on cancel too
                    };
                }
            });
        },

        add_new_page() {
            if (!this.is_mkc2_container || !this.container_data) {
                return;
            }
            
            // Check for unsaved changes using the improved page switch modal
            if (this.document_has_unsaved_changes) {
                this.show_page_switch_unsaved_modal('new_page');
                return;
            }
            
            // No unsaved changes, proceed directly
            this.validate_and_create_new_page();
        },

        async validate_and_create_new_page() {
            try {
                // Ask server for permission to create new page
                const current_page_count = this.container_data.pages.length;
                const permission_check = await this.check_premium_limit('add_page', {
                    current_page_count: current_page_count,
                    container_pages_count: current_page_count + 1
                });
                
                if (!permission_check) {
                    // Server denied permission, modal already shown by check_premium_limit
                    return;
                }
                
                // Save current page to container before creating new one
                if (this.container_data.pages[this.current_page_index]) {
                    const snapshot = this.create_snapshot();
                    const current_page_data = {
                        format_version: "1.0",
                        created_at: this.container_data.pages[this.current_page_index].mkc_data?.created_at || new Date().toISOString(),
                        last_modified: new Date().toISOString(),
                        document: {
                            canvas: {
                                width: snapshot.canvas_width,
                                height: snapshot.canvas_height,
                                background_color: snapshot.background_color,
                                outline_color: snapshot.outline_color,
                                outline_width: snapshot.outline_width,
                                page_margin: snapshot.page_margin,
                                format_id: this.current_format
                            },
                            panels: snapshot.panels_data
                        }
                    };
                    this.container_data.pages[this.current_page_index].mkc_data = current_page_data;
                }
                
                // Add new page entry to container
                const new_page_index = this.container_data.pages.length;
                const current_time = new Date().toISOString();
                const new_page = {
                    page_title: `Page ${new_page_index + 1}`,
                    page_created_at: current_time,
                    page_last_modified: current_time,
                    mkc_data: null // Will be populated immediately after canvas creation
                };
                this.container_data.pages.push(new_page);
                this.container_data.last_modified = current_time;
                this.current_page_index = new_page_index;
                
                // Use create_new_document_with_options with add_page mode (reuses ALL the logic)
                this.create_new_document_with_options(true);
                
                // CRITICAL: Populate mkc_data immediately after canvas creation
                const initial_snapshot = this.create_snapshot();
                const initial_document_data = {
                    format_version: "1.0",
                    created_at: current_time,
                    last_modified: current_time,
                    document: {
                        canvas: {
                            width: initial_snapshot.canvas_width,
                            height: initial_snapshot.canvas_height,
                            background_color: initial_snapshot.background_color,
                            outline_color: initial_snapshot.outline_color,
                            outline_width: initial_snapshot.outline_width,
                            page_margin: initial_snapshot.page_margin,
                            format_id: this.current_format
                        },
                        panels: initial_snapshot.panels_data
                    }
                };
                this.container_data.pages[new_page_index].mkc_data = initial_document_data;
                
                // Reset autosave copy to sync with new page structure
                this.container_data_autosave = null;
                                
            } catch (error) {
                console.error('Error validating new page creation:', error);
                this.show_error_message(this.get_t('error.title'), this.get_t('error.failed_to_create_page'));
            }
        },

        confirm_delete_page(page_index) {
            // Prevent deletion if there's only one page
            if (this.container_pages.length <= 1) {
                this.show_error_message(this.get_t('error.title'), this.get_t('pages.cannot_delete_last_page'));
                return;
            }
            
            // Show confirmation popup
            this.show_confirmation_popup(
                this.get_t('pages.delete_page'),
                this.get_t('pages.delete_page_confirm'),
                () => this.delete_page(page_index),
                () => {} // Cancel button - do nothing
            );
        },

        async delete_page(page_index) {
            if (!this.is_mkc2_container || !this.container_data || !this.container_data.pages) {
                return;
            }
            
            // Prevent deletion if there's only one page
            if (this.container_data.pages.length <= 1) {
                this.show_error_message(this.get_t('error.title'), this.get_t('pages.cannot_delete_last_page'));
                return;
            }
            
            try {
                // ALWAYS save current page before any modification using the existing function
                // This ensures all changes are properly saved using the same logic as page switching
                if (this.document_has_unsaved_changes) {
                    await this.perform_page_switch(this.current_page_index, true);
                }
                
                // Remove the page from container data
                this.container_data.pages.splice(page_index, 1);
                this.container_data.last_modified = new Date().toISOString();
                
                // Update page titles to maintain sequential numbering
                this.container_data.pages.forEach((page, index) => {
                    page.page_title = `Page ${index + 1}`;
                });
                
                // Adjust current page index if necessary
                let new_current_index = this.current_page_index;
                if (page_index <= this.current_page_index) {
                    // If we deleted a page before or at current position, adjust index
                    new_current_index = Math.max(0, this.current_page_index - 1);
                }
                // If we deleted a page after current position, index stays the same
                // but we need to ensure it's not beyond the new array length
                new_current_index = Math.min(new_current_index, this.container_data.pages.length - 1);
                
                // Update the current page index
                this.current_page_index = new_current_index;
                
                // Load the target page using existing logic
                const target_page = this.container_data.pages[this.current_page_index];
                if (target_page && target_page.mkc_data) {
                    await this.load_page_data(target_page.mkc_data, false);
                } else {
                    // If target page has no data, create a new document
                    this.create_new_document_with_options(true);
                }
                
                // Mark document as having unsaved changes (the container structure changed)
                this.document_has_unsaved_changes = true;
                
            } catch (error) {
                console.error('Error deleting page:', error);
                this.show_error_message(this.get_t('error.title'), `Error deleting page: ${error.message}`);
            }
        },

        async open_from_recent(file_info) {
            this.check_unsaved_changes(async () => {
                try {
                    let file_handle = file_info.handle;
                    
                    if (!file_handle || typeof file_handle.getFile !== 'function') {
                        file_handle = await this.get_file_handle(file_info.name);
                    }
                    
                    if (file_handle && typeof file_handle.getFile === 'function') {
                        this.current_file_handle = file_handle;
                        await this.load_document_from_handle(file_handle);
                        
                        const file_index = this.recent_files.findIndex(f => f.name === file_info.name);
                        if (file_index !== -1) {
                            this.recent_files[file_index].handle = file_handle;
                            this.recent_files[file_index].last_opened = new Date().toISOString();
                            this.save_recent_files();
                        }
                    } else {
                        // No valid handle found, prompt user to select file
                        this.status_message = this.get_t('status.file_access_lost_select_again');
                        await this.open_document();
                    }
                } catch (error) {
                    console.error('Error opening recent file:', error);
                    if (error.name === 'NotAllowedError' || error.name === 'NotFoundError' || 
                        error.message.includes('not found') || error.message.includes('permission') ||
                        error.message.includes('Permission denied')) {
                        // Remove from recent files and show helpful message
                        this.recent_files = this.recent_files.filter(f => f.name !== file_info.name);
                        this.save_recent_files();
                        this.status_message = this.get_t('status.file_access_lost_removed_from_recent');
                    } else {
                        this.status_message = this.get_t('status.error_loading_document');
                    }
                }
            });
        },

    }
};